import React, { useState, useEffect, useContext, useRef, useMemo, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { Box, Tooltip } from '@mui/material';
import api from '../utils/api';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  DeleteOutline as DeleteOutlineIcon,
  SwapHoriz as SwapHorizIcon,
  LabelOutlined as LabelOutlinedIcon,
  Link as LinkIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import EquipmentTypePicker from './EquipmentTypePicker';
import DescribesPicker, { renderTextLabelOption } from './DescribesPicker';
import { rankTextLabelOptions } from '../utils/textLabelOptions';
import { suggestNameLabel, linkedNameLabel, suggestDescriptionLabels } from '../utils/nameSuggestion';
import { getLabelForType } from '../data/equipmentTypes';
import { typeCounts, applyTypeFilters, matchesStatus } from '../utils/listFilters';
import { dropReplacedLinks } from '../utils/replacedLinks';
import { getVlmState, VLM_COLORS, VLM_LABELS } from '../utils/vlmStatus';
import { FAILURE_MODES } from '../data/failureModes';
import { AuthContext } from '../contexts/AuthContext';

// 'describes'/'names' edges are annotation pointers (a text label naming or
// describing an element or pipe), not physical pipe/signal routing — they
// should always render as a direct line, never the orthogonal (90°) bend
// ConnectionCreationOverlay.js inserts for 'flows to'/'controls' connections.
// See handleSaveConnection below: Connection.js already falls back to a
// straight source→target line whenever metadata.path_data is absent, so
// clearing it on save is the entire fix — no new rendering logic needed.
const DIRECT_ROUTE_RELATIONSHIP_TYPES = ['describes', 'names'];

const FAILURE_MODE_LABELS = Object.fromEntries(FAILURE_MODES.map(f => [f.value, f.label]));
// Sorted for display regardless of source-array order — this is a dropdown
// people pick from quickly, so scan order matters, but nobody should have to
// remember to hand-maintain alphabetical order in failureModes.js to get it.
const SORTED_FAILURE_MODES = [...FAILURE_MODES].sort((a, b) => a.label.localeCompare(b.label));
// Split by entity so the reject picker only shows modes relevant to what's
// selected — an `entity: null` mode (see failureModes.js) has no dedicated
// instance-level trigger yet, so it stays visible from both sides.
const SORTED_FAILURE_MODES_BY_ENTITY = {
  element: SORTED_FAILURE_MODES.filter(fm => fm.entity == null || fm.entity === 'element'),
  connection: SORTED_FAILURE_MODES.filter(fm => fm.entity == null || fm.entity === 'connection'),
};

const MONO = "'IBM Plex Mono', monospace";

// ── Tiny reusable dark input ─────────────────────────────────────────────────
const DarkInput = ({ label, value, onChange, onKeyDown, type = 'text', multiline = false, rows, autoFocus, disabled, placeholder, adornment }) => (
  <Box sx={{ mb: '14px' }}>
    <Box component="label" sx={{ display: 'block', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.1em', textTransform: 'uppercase', color: '#7f93b8', mb: '6px' }}>{label}</Box>
    {multiline ? (
      <Box
        component="textarea"
        value={value}
        onChange={onChange}
        rows={rows || 3}
        disabled={disabled}
        placeholder={placeholder}
        sx={{ width: '100%', background: '#0c1a32', border: '1px solid rgba(120,150,200,.26)', borderRadius: '8px', p: '10px 12px', color: '#cdd9ee', fontSize: '13.5px', fontFamily: "'Lexend', sans-serif", outline: 'none', resize: 'vertical', boxSizing: 'border-box', '&:focus': { borderColor: '#2dd4bf' }, '&:disabled': { opacity: .5 } }}
      />
    ) : (
      <Box sx={{ position: 'relative' }}>
        <Box
          component="input"
          type={type}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          sx={{ width: '100%', background: '#0c1a32', border: '1px solid rgba(120,150,200,.26)', borderRadius: '8px', p: '10px 12px', pr: adornment ? '36px' : '12px', color: '#eaf0fb', fontSize: '14px', fontFamily: MONO, outline: 'none', boxSizing: 'border-box', '&:focus': { borderColor: '#2dd4bf' }, '&:disabled': { opacity: .5 } }}
        />
        {adornment && (
          <Box sx={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}>{adornment}</Box>
        )}
      </Box>
    )}
  </Box>
);

// ── Tiny reusable list-filter search box (no label, inline clear button) ────
const ListSearchInput = ({ value, onChange, placeholder, trailing, listKeys = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', px: '16px', pt: '10px', pb: '8px' }}>
  <Box sx={{ position: 'relative', flex: 1, minWidth: 0 }}>
    <Box
      component="input"
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      // ↑ / ↓ / Enter here move through and open the list below
      data-list-search={listKeys || undefined}
      sx={{
        width: '100%', background: '#0c1a32', border: '1px solid rgba(120,150,200,.26)', borderRadius: '8px',
        p: '7px 30px 7px 10px', color: '#eaf0fb', fontSize: '12.5px', fontFamily: MONO, outline: 'none', boxSizing: 'border-box',
        '&:focus': { borderColor: '#2dd4bf' }, '&::placeholder': { color: '#5f7398', opacity: 1 },
      }}
    />
    {value && (
      <Box component="button" onClick={() => onChange('')}
        sx={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '15px', lineHeight: 1, p: '2px 4px', '&:hover': { color: '#eaf0fb' } }}>
        ×
      </Box>
    )}
  </Box>
  {trailing}
  </Box>
);

// Opens / closes the filter controls. Filters keep applying while closed, so the
// button lights up and shows how many are set.
const FilterToggleButton = ({ open, active, onClick }) => (
  <Tooltip title={open ? 'Hide filters' : active ? `Filters (${active} active)` : 'Filter by type'}>
    <Box component="button" onClick={onClick} aria-label="Filters" aria-expanded={open}
      sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer', p: 0,
        background: open ? 'rgba(45,212,191,.16)' : '#0c1a32',
        border: `1px solid ${open || active ? 'rgba(45,212,191,.6)' : 'rgba(120,150,200,.26)'}`,
        color: open || active ? '#2dd4bf' : '#8fa3c6', '&:hover': { color: '#eaf0fb' } }}>
      <FilterListIcon sx={{ fontSize: 17 }} />
      {active > 0 && !open && (
        <Box sx={{ position: 'absolute', top: '-5px', right: '-5px', minWidth: '15px', height: '15px', borderRadius: '8px',
          background: '#2dd4bf', color: '#0a1628', fontSize: '10px', fontWeight: 600, fontFamily: MONO,
          display: 'flex', alignItems: 'center', justifyContent: 'center', px: '3px', boxSizing: 'border-box' }}>
          {active}
        </Box>
      )}
    </Box>
  </Tooltip>
);

// ── List filters: what to show, and which element / connection type ─────────
const LIST_MODES = [['both', 'Both'], ['elements', 'Elements'], ['connections', 'Connections']];
const LIST_STATUSES = [['all', 'All'], ['unconfirmed', 'Unconfirmed'], ['confirmed', 'Confirmed']];

const Segmented = ({ options, value, onChange }) => (
  <Box sx={{ display: 'flex', border: '1px solid rgba(120,150,200,.26)', borderRadius: '8px', overflow: 'hidden' }}>
    {options.map(([v, label]) => (
      <Box key={v} component="button" onClick={() => onChange(v)}
        sx={{ flex: 1, border: 'none', p: '5px 0', cursor: 'pointer', fontSize: '11.5px', fontFamily: "'Lexend', sans-serif",
          background: value === v ? 'rgba(45,212,191,.16)' : 'transparent', color: value === v ? '#2dd4bf' : '#8fa3c6',
          '&:hover': { color: '#eaf0fb' } }}>
        {label}
      </Box>
    ))}
  </Box>
);
const filterSelectSx = {
  width: '100%', background: '#0c1a32', border: '1px solid rgba(120,150,200,.26)', borderRadius: '8px',
  p: '6px 8px', color: '#cdd9ee', fontSize: '12px', fontFamily: MONO, outline: 'none', cursor: 'pointer',
  '&:focus': { borderColor: '#2dd4bf' },
};

// options: [{ value, count }]; a selected value no longer present stays listed (count 0) so it can be cleared.
const TypeSelect = ({ value, onChange, options, allLabel, labelFor, title }) => {
  const shown = value && !options.some(o => o.value === value) ? [...options, { value, count: 0 }] : options;
  return (
    <Box component="select" value={value} title={title} onChange={(e) => onChange(e.target.value)}
      sx={{ ...filterSelectSx, ...(value ? { borderColor: 'rgba(45,212,191,.6)', color: '#2dd4bf' } : {}) }}>
      <option value="">{allLabel}</option>
      {shown.map(o => <option key={o.value} value={o.value}>{`${labelFor(o.value)} (${o.count})`}</option>)}
    </Box>
  );
};

const ListFilterBar = ({ mode, onMode, status, onStatus, elementType, onElementType, elementOptions, connectionType, onConnectionType, connectionOptions }) => (
  <Box sx={{ px: '16px', pb: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <Segmented options={LIST_MODES} value={mode} onChange={onMode} />
    <Segmented options={LIST_STATUSES} value={status} onChange={onStatus} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <TypeSelect value={elementType} onChange={onElementType} options={elementOptions} allLabel="All element types"
        labelFor={v => getLabelForType(v || 'node')} title="Element type — also narrows connections to those touching one" />
      <TypeSelect value={connectionType} onChange={onConnectionType} options={connectionOptions} allLabel="All connection types"
        labelFor={v => v || '(none)'} title="Connection type — also narrows elements to those at either end of one" />
    </Box>
  </Box>
);

// ── Status dot ───────────────────────────────────────────────────────────────
// Rejected takes priority over confirmed/unconfirmed — a rejected item's
// confirmed flag is meaningless (it's soft-deleted, see §0.2 item 6).
// `vlm` (rapid-annotate, utils/vlmStatus.js) colours an unconfirmed item by
// where Claude is with it; getVlmState already yields null once confirmed/rejected.
// typeConfirmed: its class was checked (e.g. on the contact sheet), its text
// links not yet — a teal dot, between unconfirmed grey and confirmed green.
const StatusDot = ({ confirmed, rejected, vlm, typeConfirmed }) => (
  <Box sx={{ width: '7px', height: '7px', borderRadius: '50%', background: rejected ? '#f4716a' : confirmed ? '#44d07b' : vlm ? VLM_COLORS[vlm] : typeConfirmed ? '#2dd4bf' : '#8fa3c6', flexShrink: 0 }} />
);

const getStatusMeta = (item) => {
  if (item?.metadata?.rejected) {
    return { label: 'REJECTED', color: '#f4a39e', bg: 'rgba(244,113,106,.12)', border: 'rgba(244,113,106,.4)' };
  }
  if (item?.confirmed) {
    return { label: 'CONFIRMED', color: '#7fe0a6', bg: 'rgba(68,208,123,.12)', border: 'rgba(68,208,123,.4)' };
  }
  const vlm = getVlmState(item);
  if (vlm) {
    const c = VLM_COLORS[vlm];
    return { label: VLM_LABELS[vlm], color: c, bg: `${c}1f`, border: `${c}66` };
  }
  if (item?.typeConfirmed) {
    return { label: 'TYPE CHECKED', color: '#5fe6d6', bg: 'rgba(45,212,191,.1)', border: 'rgba(45,212,191,.4)' };
  }
  return { label: 'UNCONFIRMED', color: '#a9bcdc', bg: 'rgba(120,150,200,.1)', border: 'rgba(120,150,200,.26)' };
};

// ── Section header (with optional action slot on the right) ──────────────────
// List view rows. On a large drawing the list holds thousands of rows, so each
// row is memoised (re-renders only when its own item changes) and the list
// renders progressively (LIST_CHUNK rows, more as the end scrolls into view).
const LIST_CHUNK = 200;

// Click previews the item (canvas zooms to it, the list stays); the chevron,
// a double-click or Enter opens it.
const rowSx = (previewed) => ({
  display: 'flex', alignItems: 'center', gap: '11px', pl: '16px', pr: '6px', py: '10px', borderBottom: '1px solid rgba(120,150,200,.08)',
  cursor: 'pointer', userSelect: 'none',
  background: previewed ? 'rgba(45,212,191,.12)' : 'transparent',
  boxShadow: previewed ? 'inset 3px 0 0 #2dd4bf' : 'none',
  '&:hover': { background: previewed ? 'rgba(45,212,191,.16)' : 'rgba(45,212,191,.05)' },
});

const OpenChevron = ({ onOpen }) => (
  <Tooltip title="Open" placement="left">
    <Box component="button" aria-label="Open" onClick={(e) => { e.stopPropagation(); onOpen(); }}
      sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', background: 'transparent', border: 'none', color: '#5f7398', cursor: 'pointer', p: '2px', borderRadius: '6px', '&:hover': { color: '#2dd4bf', background: 'rgba(45,212,191,.1)' } }}>
      <ChevronRightIcon sx={{ fontSize: 18 }} />
    </Box>
  </Tooltip>
);

const ElementListRow = memo(function ElementListRow({ el, previewed, onPreview, onOpen }) {
  const nameBox = (
    <Box sx={{ fontFamily: MONO, fontSize: '11.5px', color: '#5fe6d6', flexShrink: 1, minWidth: '32px', maxWidth: '140px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{el.name || '—'}</Box>
  );
  return (
    <Box data-row-id={`element:${el._id}`} aria-selected={previewed} onClick={() => onPreview(el)} onDoubleClick={() => onOpen(el)} sx={rowSx(previewed)}>
      {el.name && el.name.length > 14 ? <Tooltip title={el.name} placement="top">{nameBox}</Tooltip> : nameBox}
      <Box sx={{ fontSize: '13px', color: '#8fa3c6', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getLabelForType(el.elementType || 'node')}</Box>
      <StatusDot confirmed={el.confirmed} rejected={el.metadata?.rejected} vlm={getVlmState(el)} typeConfirmed={el.typeConfirmed} />
      <OpenChevron onOpen={() => onOpen(el)} />
    </Box>
  );
});

const ConnectionListRow = memo(function ConnectionListRow({ conn, label, previewed, onPreview, onOpen }) {
  return (
    <Box data-row-id={`connection:${conn._id}`} aria-selected={previewed} onClick={() => onPreview(conn)} onDoubleClick={() => onOpen(conn)} sx={rowSx(previewed)}>
      <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#f5a524', flexShrink: 0 }}>→</Box>
      <Box sx={{ fontSize: '13px', color: '#8fa3c6', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Box>
      <StatusDot confirmed={conn.confirmed} rejected={conn.metadata?.rejected} />
      <OpenChevron onOpen={() => onOpen(conn)} />
    </Box>
  );
});

// Loads the next chunk when it scrolls into view; also a plain button, so it
// works wherever IntersectionObserver doesn't.
const LoadMoreRows = ({ remaining, onMore }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver(entries => { if (entries.some(e => e.isIntersecting)) onMore(); });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [onMore]);
  return (
    <Box ref={ref} component="button" onClick={onMore}
      sx={{ width: '100%', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(120,150,200,.08)', color: '#7f93b8', fontFamily: MONO, fontSize: '11px', py: '10px', cursor: 'pointer', '&:hover': { color: '#2dd4bf' } }}>
      {remaining} more…
    </Box>
  );
};

const SectionHeader = ({ label, count, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '16px', py: '9px', borderBottom: '1px solid rgba(120,150,200,.12)' }}>
    <Box sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.12em', textTransform: 'uppercase', color: '#7f93b8', userSelect: 'none' }}>{label}</Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
      {action}
      {count != null && <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#5f7398' }}>{count}</Box>}
    </Box>
  </Box>
);

// ── Shared helper ─────────────────────────────────────────────────────────────
const getIdValue = (v) => (typeof v === 'object' && v?._id) ? v._id : v;

// ── Action bar button (used inside split-action cards) ────────────────────────
const ActionBtn = ({ onClick, accent, children }) => (
  <Box
    component="button"
    onClick={onClick}
    sx={{
      flex: 1, py: '10px', textAlign: 'center',
      fontSize: '12.5px', fontWeight: 500,
      fontFamily: "'Lexend', sans-serif",
      background: 'transparent', border: 'none', cursor: 'pointer',
      color: accent ? '#5fe6d6' : '#8fa3c6',
      '&:hover': accent
        ? { background: 'rgba(45,212,191,.08)', color: '#2dd4bf' }
        : { background: 'rgba(120,150,200,.06)', color: '#cdd9ee' },
    }}
  >
    {children}
  </Box>
);

// ── Main component ───────────────────────────────────────────────────────────
const DrawingSidePane = forwardRef(({
  sidebarOpen,
  toggleSidebar,
  elements,
  connections,
  hideRejected,
  selectedElement,
  selectedConnection,
  handleElementSelect,
  handleConnectionSelect,
  // the list item being looked at without opening it ({ kind, id } | null)
  previewItem,
  onPreviewItem,
  handleOpenElementEditDialog,
  handleConnectionClick,
  handleToggleConfirmedElement,
  handleToggleConfirmedConnection,
  setElements,
  setConnections,
  setSelectedElement,
  setSelectedConnection,
  notify,
  drawingId,
  // canvas pick / add-element integration
  onEnterCanvasPickMode,
  onExitCanvasPickMode,
  onAddElementForEndpoint,
  // rapid-annotate: (re-)ask the VLM to classify an element (admin only)
  onVlmClassify,
  // "Show unlinked" result (utils/graphCompleteness.js), or null when the toggle is off
  unlinked,
  // Draws not-yet-saved text links on the drawing: [{ labelId, elementId,
  // accepted }] (empty to clear) — see the suggested name/description state below.
  onLinkPreview,
}, ref) => {
  // Reject is an internal annotation action (ground-truth capture, hard-negative
  // training data), not a customer-facing feature — customers already have
  // Delete for "make this false positive go away". Gated on User.role rather
  // than environment so the same app is under test everywhere; there's no
  // admin UI yet, role is set directly in Atlas. See server/utils/roles.js.
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  // ── Existing editing state ─────────────────────────────────────────────────
  const [editingElementId, setEditingElementId] = useState(null);
  const [editingConnectionId, setEditingConnectionId] = useState(null);
  const [editElementData, setEditElementData] = useState({ name: '', description: '', elementType: 'node', confirmed: false });
  const [editConnectionData, setEditConnectionData] = useState({ name: '', description: '', relationshipType: 'flows to', confirmed: false });
  const [elementFormDirty, setElementFormDirty] = useState(false);
  const [connectionFormDirty, setConnectionFormDirty] = useState(false);

  // ── Name-field text_label picker state (Phase 1.5) — an OR alongside the
  // plain Name text field, never a replacement for it.
  const [elementNamePickerOpen, setElementNamePickerOpen] = useState(false);

  // ── Suggested name from a nearby text label (utils/nameSuggestion.js) ──────
  // Only ever a suggestion while browsing: the name field and the 'names' link
  // change only when the user accepts it (pendingNameLink, which makes the form
  // dirty) and then Saves or Confirms. Cancel, another selection, or typing a
  // different name drops it — nothing is written.
  const [pendingNameLink, setPendingNameLink] = useState(null);       // the accepted text label
  const [dismissedSuggestionId, setDismissedSuggestionId] = useState(null);
  // Same for descriptions: repeated nearby text ("GV-228", "N.O.") accepted
  // here becomes 'describes' links only on Save / Confirm.
  const [pendingDescribes, setPendingDescribes] = useState([]);
  const [dismissedDescribeIds, setDismissedDescribeIds] = useState([]);
  const [connectionNamePickerOpen, setConnectionNamePickerOpen] = useState(false);

  // ── Endpoint picker state ──────────────────────────────────────────────────
  const [editingEndpoint, setEditingEndpoint] = useState(null); // 'source' | 'target' | null
  const [pickerConnectionId, setPickerConnectionId] = useState(null);
  const [pickerMode, setPickerMode] = useState('search'); // 'search' | 'canvas'

  // ── List-view search (single box, filters Elements + Connections together) ─
  const [listSearch, setListSearch] = useState('');
  const [listMode, setListMode] = useState('both');               // 'both' | 'elements' | 'connections'
  const [listElementType, setListElementType] = useState('');     // '' = any
  const [listConnectionType, setListConnectionType] = useState('');
  const [listStatus, setListStatus] = useState('all');           // 'all' | 'unconfirmed' | 'confirmed'
  const [listFiltersOpen, setListFiltersOpen] = useState(false);
  const [listLimit, setListLimit] = useState(LIST_CHUNK);
  useEffect(() => { setListLimit(LIST_CHUNK); }, [listSearch, listMode, listStatus, listElementType, listConnectionType]);
  const showMoreRows = useCallback(() => setListLimit(n => n + 2 * LIST_CHUNK), []);

  const elementsById = useMemo(() => new Map(elements.map(e => [String(e._id), e])), [elements]);
  const connectionsById = useMemo(() => new Map(connections.map(c => [String(c._id), c])), [connections]);

  // Shared row handlers that always reach the latest select handlers.
  const listSelectRef = useRef({});
  listSelectRef.current = { handleElementSelect, handleConnectionSelect };
  listSelectRef.current.onPreviewItem = onPreviewItem;
  const onSelectListElement = useCallback((el) => listSelectRef.current.handleElementSelect(el), []);
  const onSelectListConnection = useCallback((conn) => listSelectRef.current.handleConnectionSelect(conn), []);
  const onPreviewListElement = useCallback((el) => listSelectRef.current.onPreviewItem({ kind: 'element', id: String(el._id) }), []);
  const onPreviewListConnection = useCallback((conn) => listSelectRef.current.onPreviewItem({ kind: 'connection', id: String(conn._id) }), []);
  // The list's rows in display order (elements, then connections), kept by the
  // list view's render for the keyboard: [{ kind, item }].
  const listOrderRef = useRef([]);

  useEffect(() => {
    if (selectedElement) {
      setEditingElementId(selectedElement._id);
      setEditElementData({ name: selectedElement.name || '', description: selectedElement.description || '', elementType: selectedElement.elementType || 'node', confirmed: selectedElement.confirmed || false });
      setElementFormDirty(false);
      setElementNamePickerOpen(false);
      setPendingNameLink(null);
      setDismissedSuggestionId(null);
      setPendingDescribes([]);
      setDismissedDescribeIds([]);
      setEditingConnectionId(null);
      setConnectionFormDirty(false);
    }
  }, [selectedElement]);

  // The text label already linked as the selected element's name, and a
  // suggested one when there isn't (its VLM tag read on a nearby label).
  const linkedNameLabel_ = useMemo(
    () => (selectedElement ? linkedNameLabel(selectedElement, elements, connections) : null),
    [selectedElement, elements, connections]);
  const nameSuggestion = useMemo(
    () => (selectedElement ? suggestNameLabel(selectedElement, elements, connections) : null),
    [selectedElement, elements, connections]);

  const nameLabelInUse = pendingNameLink
    || (nameSuggestion && dismissedSuggestionId !== String(nameSuggestion.label._id) ? nameSuggestion.label : null);
  const describeSuggestions = useMemo(() => {
    if (!selectedElement) return [];
    const exclude = [nameLabelInUse?._id, linkedNameLabel_?._id, ...pendingDescribes.map(l => l._id), ...dismissedDescribeIds].filter(Boolean);
    return suggestDescriptionLabels(selectedElement, elements, connections, { excludeIds: exclude });
  }, [selectedElement, elements, connections, nameLabelInUse, linkedNameLabel_, pendingDescribes, dismissedDescribeIds]);

  // Show suggested (and accepted, still unsaved) links on the drawing so it's
  // clear which text each means. Only drawn — never written from here.
  const previewLinks = selectedElement ? [
    ...(nameLabelInUse ? [{ label: nameLabelInUse, accepted: Boolean(pendingNameLink) }] : []),
    ...pendingDescribes.map(label => ({ label, accepted: true })),
    ...describeSuggestions.map(label => ({ label, accepted: false })),
  ].map(({ label, accepted }) => ({ labelId: String(label._id), elementId: String(selectedElement._id), accepted })) : [];
  const previewKey = previewLinks.map(l => `${l.labelId}:${l.accepted}`).join('|');
  useEffect(() => {
    if (!onLinkPreview) return undefined;
    onLinkPreview(previewLinks);
    return () => onLinkPreview([]);
  }, [previewKey, selectedElement?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Each new selection opens at the top of the pane. The Tag/name field's
  // autoFocus would otherwise scroll the body down to it whenever a long
  // Claude's-read panel pushes it below the fold, hiding the start of the read.
  // Keyed on editingElementId/editingConnectionId, not the selection itself: the
  // selected view only mounts (and autofocuses) once those catch up a render
  // later. Passive effects run after that commit's autoFocus, so the field keeps
  // focus — typing a tag still works and scrolls it back into view.
  const bodyRef = useRef(null);
  // Back in the list, it's where it was, with the row of the item just open
  // brought into view if it isn't — e.g. after the rapid flow moved on, or when
  // the item was opened from a link.
  const listScrollRef = useRef(0);
  const lastOpenRowRef = useRef(null);
  const openRow = selectedElement ? `element:${selectedElement._id}` : selectedConnection ? `connection:${selectedConnection._id}` : null;
  useEffect(() => { if (openRow) lastOpenRowRef.current = openRow; }, [openRow]);
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    if (editingElementId || editingConnectionId) { body.scrollTop = 0; return; }
    body.scrollTop = listScrollRef.current;
    const row = previewItem ? `${previewItem.kind}:${previewItem.id}` : lastOpenRowRef.current;
    if (row) body.querySelector(`[data-row-id="${row}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [editingElementId, editingConnectionId]); // eslint-disable-line react-hooks/exhaustive-deps
  const onBodyScroll = useCallback((e) => {
    if (!selectedElement && !selectedConnection) listScrollRef.current = e.currentTarget.scrollTop;
  }, [selectedElement, selectedConnection]);

  useEffect(() => {
    if (selectedConnection) {
      setEditingConnectionId(selectedConnection._id);
      setEditConnectionData({ name: selectedConnection.name || '', description: selectedConnection.description || '', relationshipType: selectedConnection.relationshipType || 'flows to', confirmed: selectedConnection.confirmed || false });
      setConnectionFormDirty(false);
      setConnectionNamePickerOpen(false);
      setEditingElementId(null);
      setElementFormDirty(false);
    }
  }, [selectedConnection]);

  // Esc: close picker → back to the list (as Back) → clear a list preview
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== 'Escape') return;
      if (editingEndpoint !== null) { handleCancelPicker(); return; }
      if (selectedElement || selectedConnection) backToList();
      else if (previewItem) onPreviewItem(null);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [editingEndpoint, selectedElement, selectedConnection, previewItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // List view keys: ↑ / ↓ move the preview, Enter or → opens it. Ignored while
  // typing anywhere but the list's own search box.
  useEffect(() => {
    if (!sidebarOpen || selectedElement || selectedConnection) return;
    const handler = (e) => {
      if (!['ArrowDown', 'ArrowUp', 'Enter', 'ArrowRight'].includes(e.key)) return;
      const t = e.target;
      const typing = t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable;
      if (typing && !t.closest?.('[data-list-search]')) return;
      if (typing && e.key === 'ArrowRight') return;   // moves the cursor in the search box
      if (t.tagName === 'BUTTON' || t.closest?.('[role="listbox"],[role="menu"],[role="dialog"]')) return;
      const order = listOrderRef.current;
      if (!order.length) return;
      const at = previewItem ? order.findIndex(r => r.kind === previewItem.kind && String(r.item._id) === previewItem.id) : -1;
      if (e.key === 'Enter' || e.key === 'ArrowRight') {
        if (at < 0) return;
        e.preventDefault();
        const { kind, item } = order[at];
        if (kind === 'element') handleElementSelect(item); else handleConnectionSelect(item);
        return;
      }
      e.preventDefault();
      const next = at < 0 ? 0 : Math.max(0, Math.min(order.length - 1, at + (e.key === 'ArrowDown' ? 1 : -1)));
      const { kind, item } = order[next];
      onPreviewItem({ kind, id: String(item._id) });
      setListLimit(n => Math.max(n, next + 1 + LIST_CHUNK));   // make sure its row is rendered
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sidebarOpen, selectedElement, selectedConnection, previewItem]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the previewed row in view as the preview moves.
  useEffect(() => {
    if (!previewItem || selectedElement || selectedConnection) return;
    bodyRef.current?.querySelector(`[data-row-id="${previewItem.kind}:${previewItem.id}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [previewItem]); // eslint-disable-line react-hooks/exhaustive-deps

  useImperativeHandle(ref, () => ({
    deleteSelectedElement: (element) => handleDeleteElement(element),
    deleteSelectedConnection: (connection) => handleDeleteConnection(connection),
    // DrawingView asks before replacing selectedElement with a VLM result —
    // replacing it re-seeds this form and would discard unsaved edits.
    isElementFormDirty: () => elementFormDirty,
  })); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Picker helpers ─────────────────────────────────────────────────────────
  const handleCancelPicker = () => {
    setEditingEndpoint(null);
    setPickerConnectionId(null);
    setPickerMode('search');
    if (onExitCanvasPickMode) onExitCanvasPickMode();
  };

  const handleOpenPicker = (connectionId, which) => {
    setPickerConnectionId(connectionId);
    setEditingEndpoint(which);
    setPickerMode('search');
  };

  // Sets one endpoint of a connection to newElement, marks unconfirmed, shows toast
  const handleSetEndpoint = async (connectionId, which, newElement) => {
    const conn = connections.find(c => c._id === connectionId);
    if (!conn || !newElement) return;
    try {
      const { data } = await api.put(`/api/connections/${connectionId}`, {
        source: getIdValue(conn.source),
        target: getIdValue(conn.target),
        name: conn.name,
        relationshipType: conn.relationshipType,
        description: conn.description,
        [which]: newElement._id,
        confirmed: false,
      });
      setConnections(connections.map(c => c._id === connectionId ? data : c));
      if (selectedConnection?._id === connectionId) {
        setSelectedConnection(data);
        setEditConnectionData(d => ({ ...d, confirmed: false }));
      }
      // Re-pointing a 'labels' connection's source/target can rename its
      // target — see applyConnectionNameOverwrite.
      applyConnectionNameOverwrite(data);
      handleCancelPicker();
      notify(`Re-pointed → ${newElement.name || 'element'}`);
    } catch {
      notify('Failed to update connection. Please try again.', { tone: 'error' });
    }
  };

  const handleReverseDirection = async () => {
    if (!selectedConnection) return;
    const sourceId = getIdValue(selectedConnection.source);
    const targetId = getIdValue(selectedConnection.target);
    // source/target flipping alone doesn't move the arrowhead — Connection.js
    // draws the line/arrow from metadata.path_data.path (start→end), entirely
    // independent of which element is currently `source`/`target`. Reverse the
    // segment order and each segment's start/end too, same technique as
    // handleRerouteComplete in DrawingView.js.
    const path = selectedConnection.metadata?.path_data?.path;
    const metadata = Array.isArray(path) && path.length > 0
      ? {
          ...selectedConnection.metadata,
          path_data: {
            ...selectedConnection.metadata.path_data,
            path: [...path].reverse().map(seg => ({
              orientation: seg.orientation,
              start: seg.end,
              end: seg.start,
            })),
          },
        }
      : undefined;
    try {
      const { data } = await api.put(`/api/connections/${selectedConnection._id}`, {
        source: targetId,
        target: sourceId,
        name: selectedConnection.name,
        relationshipType: selectedConnection.relationshipType,
        description: selectedConnection.description,
        confirmed: false,
        ...(metadata ? { metadata } : {}),
      });
      setConnections(connections.map(c => c._id === selectedConnection._id ? data : c));
      setSelectedConnection(data);
      setEditConnectionData(d => ({ ...d, confirmed: false }));
      applyConnectionNameOverwrite(data);
      notify('Direction reversed');
    } catch {
      notify('Failed to reverse direction. Please try again.', { tone: 'error' });
    }
  };

  // ── Form dirty checks ──────────────────────────────────────────────────────
  const checkElementFormDirty = (d, pending = pendingNameLink, describes = pendingDescribes) => {
    if (!selectedElement) return false;
    return Boolean(pending) || describes.length > 0 || d.name !== (selectedElement.name || '') ||
           d.description !== (selectedElement.description || '') ||
           d.elementType !== (selectedElement.elementType || 'node');
  };

  const checkConnectionFormDirty = (d) => {
    if (!selectedConnection) return false;
    return d.name !== (selectedConnection.name || '') ||
           d.description !== (selectedConnection.description || '') ||
           d.relationshipType !== (selectedConnection.relationshipType || 'flows to');
  };

  // ── Derived filtered data ──────────────────────────────────────────────────
  const getFilteredData = () => {
    if (selectedElement) {
      const relatedConnections = connections.filter(conn => {
        if (!conn.source || !conn.target) return false;
        return String(getIdValue(conn.source)) === String(selectedElement._id) ||
               String(getIdValue(conn.target)) === String(selectedElement._id);
      });
      const relatedElementIds = new Set([String(selectedElement._id)]);
      relatedConnections.forEach(conn => {
        if (conn.source?._id) relatedElementIds.add(String(conn.source._id));
        if (conn.target?._id) relatedElementIds.add(String(conn.target._id));
      });
      return { filteredElements: elements.filter(el => relatedElementIds.has(String(el._id))), filteredConnections: relatedConnections };
    } else if (selectedConnection) {
      const sourceEl = elements.find(el => String(el._id) === String(getIdValue(selectedConnection.source)));
      const targetEl = elements.find(el => String(el._id) === String(getIdValue(selectedConnection.target)));
      return { filteredElements: [sourceEl, targetEl].filter(Boolean), filteredConnections: [selectedConnection] };
    }
    return { filteredElements: elements, filteredConnections: connections };
  };

  const { filteredElements, filteredConnections } = getFilteredData();

  // ── List-view search matching (Elements/Connections columns) ───────────────
  // Only applied in the no-selection "LIST VIEW" branch below — getFilteredData's
  // other branches (Connected section, endpoint cards) are untouched.
  const elementMatchesSearch = (el, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (el.name || '').toLowerCase().includes(q) ||
           getLabelForType(el.elementType || 'node').toLowerCase().includes(q) ||
           (el.description || '').toLowerCase().includes(q);
  };
  const connectionMatchesSearch = (conn, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const derivedName = `${getElementName(conn.source)} → ${getElementName(conn.target)}`;
    return (conn.name || '').toLowerCase().includes(q) ||
           (conn.relationshipType || '').toLowerCase().includes(q) ||
           derivedName.toLowerCase().includes(q);
  };

  // ── API handlers ───────────────────────────────────────────────────────────
  // Any connection PUT/POST that changes source/target/relationshipType can
  // trigger applyLabelNameDerivation server-side (a `labels` connection renames
  // its target) — the response carries that as `nameOverwrite: {oldName, newName}`.
  // The local elements/connections cache must be patched to match, or the
  // sidebar/canvas keep showing the stale name until a full reload. Shared by
  // every connection-mutating handler below that can plausibly trigger it.
  // It also drops the links the server replaced (a text names a thing or
  // describes it, never both — see utils/replacedLinks.js).
  const applyConnectionNameOverwrite = (data) => {
    if (data?.replacedLinks?.length) setConnections(prev => dropReplacedLinks(prev, data));
    const overwrite = data?.nameOverwrite;
    if (!overwrite) return;
    const targetId = getIdValue(data.target);
    if (data.targetType === 'Connection') {
      setConnections(prev => prev.map(c => c._id === targetId ? { ...c, name: overwrite.newName } : c));
    } else {
      setElements(prev => prev.map(el => el._id === targetId ? { ...el, name: overwrite.newName } : el));
      if (selectedElement?._id === targetId) {
        setSelectedElement(prev => ({ ...prev, name: overwrite.newName }));
        setEditElementData(prev => ({ ...prev, name: overwrite.newName }));
      }
    }
  };

  // `silent`: skip the notify/close-the-form side effects — used when this
  // save is a prerequisite step inside another action (see the name
  // picker's handlePick below) rather than the user's own explicit Save.
  // The accepted suggested name becomes a real 'names' link only here, after
  // the element itself has saved (the server derives the same name from it).
  const createPendingNameLink = async (elementId) => {
    const label = pendingNameLink;
    setPendingNameLink(null);
    if (!label) return;
    // A name is singular: re-point an existing name link (upgrading a legacy
    // 'labels' one) rather than adding a second.
    // Only a live one: a rejected link stays as it was rejected (a new one is made instead).
    const existing = connections.find(c => ['names', 'labels'].includes(c.relationshipType) && !c.metadata?.rejected && String(getIdValue(c.target)) === String(elementId));
    try {
      const { data } = existing
        ? await api.put(`/api/connections/${existing._id}`, { source: label._id, relationshipType: 'names' })
        : await api.post('/api/connections', {
            drawing: drawingId, source: label._id, target: elementId, targetType: 'Element',
            relationshipType: 'names', confirmed: true,
          });
      setConnections(prev => existing ? prev.map(c => c._id === data._id ? data : c) : [...prev, data]);
      if (data?.replacedLinks?.length) setConnections(prev => dropReplacedLinks(prev, data));
    } catch {
      notify(`Saved, but linking the text "${label.name}" failed. Please link it again.`, { tone: 'error' });
    }
  };

  // A describes link that took the place of the same text's names link.
  const notifyMovedFromName = (label, data) => {
    if (!data?.replacedLinks?.some(l => ['names', 'labels'].includes(l.relationshipType))) return false;
    notify(`Moved "${label.name || 'label'}" from name to description`);
    return true;
  };

  // Returns the element's name if a describes link cleared it (it was that
  // text's name), else undefined — callers holding the element from before
  // must use it, or they'd put the old name back.
  const createPendingDescribes = async (elementId) => {
    const labels = pendingDescribes;
    setPendingDescribes([]);
    let clearedName;
    for (const label of labels) {
      try {
        const { data } = await api.post('/api/connections', {
          drawing: drawingId, source: label._id, target: elementId, targetType: 'Element',
          relationshipType: 'describes', confirmed: true,
        });
        setConnections(prev => [...prev, data]);
        applyConnectionNameOverwrite(data);
        if (notifyMovedFromName(label, data) && data.nameOverwrite) clearedName = data.nameOverwrite.newName;
      } catch {
        notify(`Saved, but linking the description "${label.name}" failed. Please link it again.`, { tone: 'error' });
      }
    }
    return clearedName;
  };

  const handleSaveElement = async ({ silent = false } = {}) => {
    if (!selectedElement) return null;
    try {
      const { data } = await api.put(`/api/elements/${selectedElement._id}`, { ...selectedElement, ...editElementData });
      setElements(elements.map(el => el._id === selectedElement._id ? data : el));
      await createPendingNameLink(data._id);
      const clearedName = await createPendingDescribes(data._id);
      setSelectedElement(clearedName === undefined ? data : { ...data, name: clearedName });
      setElementFormDirty(false);
      if (!silent) {
        setEditingElementId(null);
        notify('Element updated successfully!');
      }
      return data;
    } catch {
      notify('Failed to update element. Please try again.', { tone: 'error' });
      return null;
    }
  };

  // `silent`: skip the notify/close-the-form side effects — used when this
  // save is a prerequisite step inside another action (see the name
  // picker's handlePick below) rather than the user's own explicit Save.
  const handleSaveConnection = async ({ silent = false } = {}) => {
    if (!selectedConnection) return null;
    try {
      // source/target must be re-extracted to bare ids even though selectedConnection
      // is spread first — the server populates both fields on every connection
      // response (create *and* update), so selectedConnection may already be holding
      // full nested objects here rather than ids. Spreading it as-is would silently
      // send those objects back and the server rejects them ("Invalid source id").
      const effectiveRelationshipType = editConnectionData.relationshipType ?? selectedConnection.relationshipType;
      // A connection drawn on the canvas always starts as 'flows to' (the
      // Connection tool has no way to pick a type mid-draw) and only becomes
      // 'describes'/'names' afterward via this form's dropdown — so *this*
      // save is the first point that knows the final type, and the right
      // place to drop any orthogonal path left over from that initial draw.
      // Omitting path_data (undefined is stripped by JSON.stringify) rather
      // than merging it in also matters server-side: updateConnection takes
      // a metadata field, if present, as the full replacement rather than
      // merging with what's stored, so this is enough to actually clear it.
      const metadata = DIRECT_ROUTE_RELATIONSHIP_TYPES.includes(effectiveRelationshipType)
        ? { ...selectedConnection.metadata, path_data: undefined }
        : selectedConnection.metadata;
      const { data } = await api.put(`/api/connections/${selectedConnection._id}`, {
        ...selectedConnection,
        ...editConnectionData,
        metadata,
        source: getIdValue(selectedConnection.source),
        target: getIdValue(selectedConnection.target),
      });
      setConnections(connections.map(c => c._id === selectedConnection._id ? data : c));
      setSelectedConnection(data);
      setConnectionFormDirty(false);
      // Setting relationshipType to 'labels' here (e.g. after drawing a plain
      // connection and switching its type) can rename the target — see
      // applyConnectionNameOverwrite.
      applyConnectionNameOverwrite(data);

      if (!silent) {
        setEditingConnectionId(null);
        notify('Connection updated successfully!');
      }
      return data;
    } catch {
      notify('Failed to update connection. Please try again.', { tone: 'error' });
      return null;
    }
  };

  // Orphan connections legitimately have a null/undefined source or target — must stay
  // undefined (dropped by JSON.stringify) rather than becoming the literal string
  // "undefined", which the server would try to cast to an ObjectId and crash on.
  const getElId = (v) => {
    if (v === null || v === undefined) return undefined;
    return v?._id ? String(v._id) : String(v);
  };

  const handleDeleteElement = async (element) => {
    const elementId = String(element._id);
    // Mirrors elementController.deleteElement: names/describes links to or from
    // the element are deleted with it; pipes and signal lines keep their route
    // as orphans (endpoint unset, metadata.is_orphan) so they can be re-attached.
    const touching = connections.filter(
      c => getElId(c.source) === elementId || getElId(c.target) === elementId
    );
    const isTextLink = c => ['names', 'describes', 'labels'].includes(c.relationshipType);
    const textLinks = touching.filter(isTextLink);
    const affectedConns = touching.filter(c => !isTextLink(c));
    const orphanedConns = affectedConns.map(c => ({
      ...c,
      source: getElId(c.source) === elementId ? undefined : c.source,
      target: getElId(c.target) === elementId ? undefined : c.target,
      metadata: { ...c.metadata, is_orphan: true },
    }));

    setElements(prev => prev.filter(el => String(el._id) !== elementId));
    setConnections(prev => prev
      .filter(c => !textLinks.some(t => t._id === c._id))
      .map(c => orphanedConns.find(oc => oc._id === c._id) || c));
    setSelectedElement(null); setSelectedConnection(null);
    setEditingElementId(null); setEditingConnectionId(null);

    try {
      await api.delete(`/api/elements/${element._id}`);

      notify(`"${element.name || 'Element'}" deleted`, { undo: async () => {
        try {
          const { _id, __v, ...elData } = element;
          const { data: newEl } = await api.post('/api/elements', { ...elData, drawing: elData.drawing?._id ?? elData.drawing });
          setElements(prev => [...prev, newEl]);
          setSelectedElement(newEl);

          // The deleted names/describes links are re-created against the new element.
          await Promise.all(textLinks.map(async link => {
            try {
              const { _id: _linkId, __v: _linkV, ...linkData } = link;
              const { data: recreated } = await api.post('/api/connections', {
                ...linkData,
                drawing: linkData.drawing?._id ?? linkData.drawing,
                source: getElId(link.source) === elementId ? newEl._id : getElId(link.source),
                target: getElId(link.target) === elementId ? newEl._id : getElId(link.target),
              });
              setConnections(prev => [...prev, recreated]);
            } catch {}
          }));
          // Re-attach the same (still-existing, now-orphaned) pipes rather than
          // creating new ones — those were never deleted, only orphaned.
          await Promise.all(affectedConns.map(async conn => {
            try {
              const { data: updatedConn } = await api.put(`/api/connections/${conn._id}`, {
                ...conn,
                source: getElId(conn.source) === elementId ? newEl._id : getElId(conn.source),
                target: getElId(conn.target) === elementId ? newEl._id : getElId(conn.target),
                metadata: { ...conn.metadata, is_orphan: false },
              });
              setConnections(prev => prev.map(c => c._id === updatedConn._id ? updatedConn : c));
            } catch {}
          }));
        } catch {}
      } });
    } catch {
      setElements(prev => [...prev, element]);
      setConnections(prev => [...prev.map(c => affectedConns.find(ac => ac._id === c._id) || c), ...textLinks]);
      notify('Failed to delete element. Please try again.', { tone: 'error' });
    }
  };

  // keepSelection: deleting one of the selected element's connections from its
  // Connected list — stay on the element instead of closing it.
  const handleDeleteConnection = async (connection, { keepSelection = false } = {}) => {
    // Mirrors connectionController.deleteConnection: links naming or describing
    // this pipe are deleted with it.
    const pipeLinks = connections.filter(c => c.targetType === 'Connection' && getElId(c.target) === String(connection._id));
    setConnections(prev => prev.filter(c => c._id !== connection._id && !pipeLinks.some(l => l._id === c._id)));
    if (!keepSelection) {
      setSelectedConnection(null); setSelectedElement(null);
      setEditingConnectionId(null); setEditingElementId(null);
    }

    try {
      await api.delete(`/api/connections/${connection._id}`);

      notify(`"${connection.name || 'Connection'}" deleted`, { undo: async () => {
        try {
          const { _id, __v, ...connData } = connection;
          const { data: newConn } = await api.post('/api/connections', {
            ...connData,
            drawing: connData.drawing?._id ?? connData.drawing,
            source: getElId(connData.source),
            target: getElId(connData.target),
          });
          setConnections(prev => [...prev, newConn]);
          if (!keepSelection) setSelectedConnection(newConn);
          // ...and the links that named or described it, pointed at the new pipe.
          await Promise.all(pipeLinks.map(async link => {
            try {
              const { _id: _linkId, __v: _linkV, ...linkData } = link;
              const { data: recreated } = await api.post('/api/connections', {
                ...linkData,
                drawing: linkData.drawing?._id ?? linkData.drawing,
                source: getElId(link.source),
                target: newConn._id,
              });
              setConnections(prev => [...prev, recreated]);
            } catch {}
          }));
        } catch {}
      } });
    } catch {
      setConnections(prev => [...prev, connection, ...pipeLinks]);
      notify('Failed to delete connection. Please try again.', { tone: 'error' });
    }
  };

  // Links an existing text_label to `targetItem` as a `describes` connection
  // (DescribesPicker, Description field) — unlike the Name-field picker,
  // this never re-points an existing edge; a target can have several
  // description labels (that's the point — the same label text can describe
  // many different elements), so every pick is a genuinely new connection,
  // guarded only against an exact-duplicate re-pick.
  const handleLinkDescribesLabel = async (targetItem, targetType, label) => {
    const alreadyLinked = connections.some(c =>
      c.relationshipType === 'describes' &&
      String(getIdValue(c.source)) === String(label._id) &&
      String(getIdValue(c.target)) === String(targetItem._id)
    );
    if (alreadyLinked) {
      notify(`"${label.name || 'label'}" is already linked`);
      return;
    }
    try {
      const { data } = await api.post('/api/connections', {
        drawing: drawingId,
        source: label._id,
        target: targetItem._id,
        targetType,
        relationshipType: 'describes',
        confirmed: true,
      });
      setConnections(prev => [...prev, data]);
      applyConnectionNameOverwrite(data);
      if (!notifyMovedFromName(label, data)) notify(`Linked "${label.name || 'label'}" as a description`);
    } catch {
      notify('Failed to link label. Please try again.', { tone: 'error' });
    }
  };

  // Unlinks one describes connection — deliberately not handleDeleteConnection,
  // which also clears the current selection/editing state; removing one
  // description label must leave whatever item you're currently editing open.
  const handleUnlinkDescribesConnection = async (conn) => {
    setConnections(prev => prev.filter(c => c._id !== conn._id));
    try {
      await api.delete(`/api/connections/${conn._id}`);
      notify(`Unlinked "${conn.name || 'label'}"`, { undo: async () => {
        try {
          const { _id, __v, ...connData } = conn;
          const { data: newConn } = await api.post('/api/connections', {
            ...connData,
            drawing: connData.drawing?._id ?? connData.drawing,
            source: getElId(connData.source),
            target: getElId(connData.target),
          });
          setConnections(prev => [...prev, newConn]);
        } catch {}
      } });
    } catch {
      setConnections(prev => [...prev, conn]);
      notify('Failed to unlink label. Please try again.', { tone: 'error' });
    }
  };

  // ── Auto-advance after confirm/reject ───────────────────────────────────
  // Jumps to the next unconfirmed, unrejected item in the same flat order the
  // "LIST VIEW" section renders (elements, then connections — see
  // getFilteredData's no-selection branch). Only called from the "marking as
  // done" direction (confirm, reject) — never from un-confirm/un-reject,
  // since picking those back off means "I want to keep looking at this one".
  // Selecting via handleElementSelect/handleConnectionSelect gets pan-to-view
  // for free (DrawingView's scrollToBBox effect is keyed on selection).
  // alsoDone: ids processed in the same action (a confirm's cascade) whose new
  // state this closure's lists don't have yet.
  const advanceToNextUnprocessed = (justProcessedId, kind, alsoDone = []) => {
    const combined = [
      ...elements.map(item => ({ item, kind: 'element' })),
      ...connections.map(item => ({ item, kind: 'connection' })),
    ];
    const done = new Set(alsoDone.map(String));
    const isUnprocessed = entry => !entry.item.confirmed && !entry.item.metadata?.rejected && !done.has(String(entry.item._id));
    const currentIndex = combined.findIndex(entry => entry.kind === kind && entry.item._id === justProcessedId);
    for (let offset = 1; offset <= combined.length; offset++) {
      const entry = combined[(currentIndex + offset) % combined.length];
      if (isUnprocessed(entry)) {
        if (entry.kind === 'element') handleElementSelect(entry.item);
        else handleConnectionSelect(entry.item);
        return;
      }
    }
    clearSelection();
  };

  // Confirming an element confirms its names/describes links with it, and the
  // text labels at their other end (plus labels just accepted as its name or
  // descriptions). Pipes and signal lines are not: confirming a symbol doesn't
  // check where its lines go, and traced lines must stay unconfirmed for
  // re-tracing to replace them. Rejected items are left alone. Returns what
  // it confirmed, for the toast and its undo.
  const TEXT_LINKS = ['names', 'describes', 'labels'];
  const confirmShownItems = async (elementId, extraLabelIds = []) => {
    const id = String(elementId);
    const live = item => item && !item.confirmed && !item.metadata?.rejected;
    const touching = connections.filter(c => TEXT_LINKS.includes(c.relationshipType)
      && [getIdValue(c.source), getIdValue(c.target)].map(String).includes(id));
    const linkIds = touching.filter(live).map(c => c._id);
    const labelIds = new Set(extraLabelIds.filter(Boolean).map(String));
    for (const c of touching) {
      if (c.metadata?.rejected) continue;
      const other = String(getIdValue(c.source)) === id ? getIdValue(c.target) : getIdValue(c.source);
      labelIds.add(String(other));
    }
    const labels = [...labelIds].map(l => elementsById.get(l)).filter(el => el?.elementType === 'text_label' && live(el));
    const put = (path, itemId) => api.put(`/api/${path}/${itemId}`, { confirmed: true }).then(r => r.data).catch(() => null);
    const [doneLinks, doneLabels] = await Promise.all([
      Promise.all(linkIds.map(cid => put('connections', cid))),
      Promise.all(labels.map(el => put('elements', el._id))),
    ]);
    const links = doneLinks.filter(Boolean), texts = doneLabels.filter(Boolean);
    if (links.length) setConnections(cs => cs.map(c => links.find(l => l._id === c._id) || c));
    if (texts.length) setElements(els => els.map(el => texts.find(t => t._id === el._id) || el));
    return { linkIds: links.map(l => l._id), labelIds: texts.map(t => t._id) };
  };

  const handleConfirmElementToggle = async () => {
    if (!selectedElement) return;
    const prev = editElementData;
    const targetId = selectedElement._id;
    const d = { ...editElementData, confirmed: !editElementData.confirmed };
    setEditElementData(d);
    try {
      const { data } = await api.put(`/api/elements/${targetId}`, { ...selectedElement, ...d });
      setElements(els => els.map(el => el._id === targetId ? data : el));
      let clearedName;
      let cascade = { linkIds: [], labelIds: [] };
      if (data.confirmed) {
        const acceptedLabelIds = [pendingNameLink?._id, ...pendingDescribes.map(l => l._id)];
        await createPendingNameLink(targetId);
        clearedName = await createPendingDescribes(targetId);
        cascade = await confirmShownItems(targetId, acceptedLabelIds);
      }
      setSelectedElement(clearedName === undefined ? data : { ...data, name: clearedName });
      if (data.confirmed) {
        const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
        const also = [cascade.linkIds.length && plural(cascade.linkIds.length, 'connection'),
          cascade.labelIds.length && plural(cascade.labelIds.length, 'text label')].filter(Boolean).join(', ');
        // Undo re-targets by id, not by current selection — the user has likely
        // already auto-advanced elsewhere by the time they click it.
        notify(also ? `Confirmed · plus ${also}` : 'Confirmed', { undo: async () => {
          try {
            const { data: reverted } = await api.put(`/api/elements/${targetId}`, { confirmed: false });
            setElements(els => els.map(el => el._id === targetId ? reverted : el));
            setSelectedElement(sel => sel?._id === targetId ? reverted : sel);
            const unconfirm = (path, itemId) => api.put(`/api/${path}/${itemId}`, { confirmed: false }).then(r => r.data).catch(() => null);
            const [links, texts] = await Promise.all([
              Promise.all(cascade.linkIds.map(cid => unconfirm('connections', cid))),
              Promise.all(cascade.labelIds.map(eid => unconfirm('elements', eid))),
            ]);
            setConnections(cs => cs.map(c => links.find(l => l?._id === c._id) || c));
            setElements(els => els.map(el => texts.find(t => t?._id === el._id) || el));
          } catch {
            notify('Failed to undo. Please try again.', { tone: 'error' });
          }
        } });
        advanceToNextUnprocessed(targetId, 'element', [...cascade.linkIds, ...cascade.labelIds]);
      }
    } catch {
      setEditElementData(prev);
      notify('Failed to update. Please try again.', { tone: 'error' });
    }
  };

  const handleConfirmConnectionToggle = async () => {
    if (!selectedConnection) return;
    const prev = editConnectionData;
    const targetId = selectedConnection._id;
    const d = { ...editConnectionData, confirmed: !editConnectionData.confirmed };
    setEditConnectionData(d);
    try {
      // See handleSaveConnection's comment — selectedConnection.source/target may
      // already be server-populated objects, not ids, so re-extract explicitly.
      const { data } = await api.put(`/api/connections/${targetId}`, {
        ...selectedConnection,
        ...d,
        source: getIdValue(selectedConnection.source),
        target: getIdValue(selectedConnection.target),
      });
      setConnections(cs => cs.map(c => c._id === targetId ? data : c));
      setSelectedConnection(data);
      if (data.confirmed) {
        notify('Confirmed', { undo: async () => {
          try {
            const { data: reverted } = await api.put(`/api/connections/${targetId}`, { confirmed: false });
            setConnections(cs => cs.map(c => c._id === targetId ? reverted : c));
            setSelectedConnection(sel => sel?._id === targetId ? reverted : sel);
          } catch {
            notify('Failed to undo. Please try again.', { tone: 'error' });
          }
        } });
        advanceToNextUnprocessed(targetId, 'connection');
      }
    } catch {
      setEditConnectionData(prev);
      notify('Failed to update. Please try again.', { tone: 'error' });
    }
  };

  // ── Reject / un-reject (soft-delete for annotation — §0.2 item 6) ──────────
  // Minimal request bodies on purpose: the server's reject branch (applyReject)
  // only reads `rejected`/`rejectionReason` off the request and ignores
  // everything else, so there's no need to round-trip the full element/
  // connection object the way the confirm-toggle handlers above do. This is
  // also deliberate for training-data integrity: a rejected record must keep
  // the CV pipeline's raw, unedited output, never any pending human edit.
  // Rejecting an element rejects its names/describes links with it (and
  // un-rejecting restores them) server-side; the response lists them as
  // linkChanges. Returns the element without that field.
  const applyLinkChanges = ({ linkChanges, ...element }) => {
    if (linkChanges?.length) {
      const byId = new Map(linkChanges.map(ch => [String(ch._id), ch]));
      setConnections(cs => cs.map(c => {
        const ch = byId.get(String(c._id));
        if (!ch) return c;
        const { rejectionReason, rejectedWith, ...meta } = c.metadata || {};
        return { ...c, metadata: ch.rejected ? { ...meta, rejected: true, rejectionReason: ch.rejectionReason, rejectedWith: element._id } : { ...meta, rejected: false } };
      }));
    }
    return element;
  };

  const handleRejectElement = async (reason) => {
    if (!selectedElement) return;
    const targetId = selectedElement._id;
    try {
      const { data: response } = await api.put(`/api/elements/${targetId}`, { rejected: true, rejectionReason: reason });
      const data = applyLinkChanges(response);
      setElements(els => els.map(el => el._id === targetId ? data : el));
      setSelectedElement(data);
      const withLinks = response.linkChanges?.length ? ` (and ${response.linkChanges.length} text link${response.linkChanges.length === 1 ? '' : 's'})` : '';
      notify(`Rejected · ${FAILURE_MODE_LABELS[reason] || reason}${withLinks}`, { undo: async () => {
        try {
          const { data: revertedResponse } = await api.put(`/api/elements/${targetId}`, { rejected: false });
          const reverted = applyLinkChanges(revertedResponse);
          setElements(els => els.map(el => el._id === targetId ? reverted : el));
          setSelectedElement(sel => sel?._id === targetId ? reverted : sel);
        } catch {
          notify('Failed to undo reject. Please try again.', { tone: 'error' });
        }
      } });
      advanceToNextUnprocessed(targetId, 'element');
    } catch {
      notify('Failed to reject. Please try again.', { tone: 'error' });
    }
  };

  const handleUnrejectElement = async () => {
    if (!selectedElement) return;
    try {
      const { data: response } = await api.put(`/api/elements/${selectedElement._id}`, { rejected: false });
      const data = applyLinkChanges(response);
      setElements(elements.map(el => el._id === selectedElement._id ? data : el));
      setSelectedElement(data);
      notify('Un-rejected');
    } catch {
      notify('Failed to undo reject. Please try again.', { tone: 'error' });
    }
  };

  const handleRejectConnection = async (reason) => {
    if (!selectedConnection) return;
    const targetId = selectedConnection._id;
    try {
      const { data } = await api.put(`/api/connections/${targetId}`, { rejected: true, rejectionReason: reason });
      setConnections(cs => cs.map(c => c._id === targetId ? data : c));
      setSelectedConnection(data);
      notify(`Rejected · ${FAILURE_MODE_LABELS[reason] || reason}`, { undo: async () => {
        try {
          const { data: reverted } = await api.put(`/api/connections/${targetId}`, { rejected: false });
          setConnections(cs => cs.map(c => c._id === targetId ? reverted : c));
          setSelectedConnection(sel => sel?._id === targetId ? reverted : sel);
        } catch {
          notify('Failed to undo reject. Please try again.', { tone: 'error' });
        }
      } });
      advanceToNextUnprocessed(targetId, 'connection');
    } catch {
      notify('Failed to reject. Please try again.', { tone: 'error' });
    }
  };

  const handleUnrejectConnection = async () => {
    if (!selectedConnection) return;
    try {
      const { data } = await api.put(`/api/connections/${selectedConnection._id}`, { rejected: false });
      setConnections(connections.map(c => c._id === selectedConnection._id ? data : c));
      setSelectedConnection(data);
      notify('Un-rejected');
    } catch {
      notify('Failed to undo reject. Please try again.', { tone: 'error' });
    }
  };

  // The address bar already names the open item (DrawingView keeps it in step).
  const copyItemLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      notify('Link copied');
    } catch {
      notify('Could not copy the link — copy it from the address bar.', { tone: 'error' });
    }
  };

  const clearSelection = () => {
    setEditingElementId(null);
    setEditingConnectionId(null);
    setSelectedElement(null);
    setSelectedConnection(null);
    handleCancelPicker();
  };

  // Back releases the item fully: it isn't left previewed, so with Show all
  // off it leaves the canvas too (the list still scrolls back to its row).
  const backToList = () => {
    clearSelection();
    onPreviewItem(null);
  };

  // ── Collapsed tab ──────────────────────────────────────────────────────────
  if (!sidebarOpen) {
    return (
      <Box
        onClick={toggleSidebar}
        sx={{
          position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
          zIndex: 100, background: '#0b1730', border: '1px solid rgba(120,150,200,.22)',
          borderRight: 'none', borderRadius: '8px 0 0 8px',
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 72, color: '#7f93b8',
          transition: 'color .15s, background .15s',
          '&:hover': { background: '#0e1e3a', color: '#eaf0fb' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18 }} />
      </Box>
    );
  }

  // Resolves a connection endpoint id against Elements first, then
  // Connections — a 'labels' edge may target a Connection (naming a pipe)
  // instead of an Element (Phase 1.5). Every place that previously assumed
  // an endpoint was always an Element needs this fallback, or a labels→pipe
  // edge silently renders blank.
  const resolveEndpoint = (end) => {
    if (!end) return null;
    const id = getIdValue(end);
    const el = elementsById.get(String(id));
    if (el) return { doc: el, kind: 'element' };
    const conn = connectionsById.get(String(id));
    if (conn) return { doc: conn, kind: 'connection' };
    return null;
  };

  const getElementName = (end) => {
    const resolved = resolveEndpoint(end);
    if (!resolved) return 'Unknown';
    if (resolved.kind === 'connection') {
      return resolved.doc.name || `${getElementName(resolved.doc.source)} → ${getElementName(resolved.doc.target)}`;
    }
    return resolved.doc.name || 'Unknown';
  };

  // ── Inline endpoint picker ─────────────────────────────────────────────────
  const renderEndpointPicker = (connectionId, which) => {
    const conn = connections.find(c => c._id === connectionId);
    const fixedId = which === 'source' ? getIdValue(conn?.target) : getIdValue(conn?.source);
    const availableElements = elements.filter(el => String(el._id) !== String(fixedId));

    return (
      <Box sx={{ borderRadius: '11px', border: '1.5px solid #2dd4bf', background: 'rgba(45,212,191,.03)', overflow: 'hidden' }}>
        {/* Picker header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '13px', py: '10px', borderBottom: '1px solid rgba(45,212,191,.18)' }}>
          <Box sx={{ fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.1em', textTransform: 'uppercase', color: '#2dd4bf' }}>
            {which === 'source' ? 'Change from element' : 'Change to element'}
          </Box>
          <Box component="button" onClick={handleCancelPicker}
            sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '14px', p: '2px 5px', borderRadius: '4px', lineHeight: 1, '&:hover': { color: '#eaf0fb' } }}>
            ✕
          </Box>
        </Box>

        {/* Search / Canvas tabs */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid rgba(120,150,200,.12)' }}>
          {['search', 'canvas'].map(mode => (
            <Box key={mode} component="button"
              onClick={() => {
                setPickerMode(mode);
                if (mode === 'canvas' && onEnterCanvasPickMode) {
                  onEnterCanvasPickMode({ connectionId, which }, (pickedEl) => handleSetEndpoint(connectionId, which, pickedEl));
                } else if (mode === 'search' && onExitCanvasPickMode) {
                  onExitCanvasPickMode();
                }
              }}
              sx={{
                flex: 1, py: '9px', textAlign: 'center',
                fontSize: '12px', fontWeight: 500, fontFamily: "'Lexend', sans-serif",
                border: 'none', cursor: 'pointer',
                color: pickerMode === mode ? '#2dd4bf' : '#7f93b8',
                background: pickerMode === mode ? 'rgba(45,212,191,.08)' : 'transparent',
                borderBottom: pickerMode === mode ? '2px solid #2dd4bf' : '2px solid transparent',
                '&:hover': { color: pickerMode === mode ? '#2dd4bf' : '#cdd9ee' },
              }}>
              {mode === 'search' ? 'Search list' : 'Pick on drawing'}
            </Box>
          ))}
        </Box>

        {/* Picker content */}
        <Box sx={{ p: '10px 12px' }}>
          {pickerMode === 'search' && (
            <Autocomplete
              openOnFocus
              autoHighlight
              options={availableElements}
              getOptionLabel={(option) => option.name || '—'}
              onChange={(_, newEl) => { if (newEl) handleSetEndpoint(connectionId, which, newEl); }}
              filterOptions={(opts, state) => {
                if (!state.inputValue) return opts;
                const q = state.inputValue.toLowerCase();
                return opts.filter(o =>
                  (o.name || '').toLowerCase().includes(q) ||
                  getLabelForType(o.elementType || '').toLowerCase().includes(q)
                );
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option._id}
                  sx={{ display: 'flex !important', alignItems: 'center !important', gap: '8px !important', px: '12px !important', py: '8px !important' }}>
                  <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#5fe6d6', minWidth: '44px' }}>{option.name || '—'}</Box>
                  <Box sx={{ fontSize: '12px', color: '#8fa3c6', flex: 1 }}>{getLabelForType(option.elementType || 'node')}</Box>
                  <StatusDot confirmed={option.confirmed} rejected={option.metadata?.rejected} vlm={getVlmState(option)} />
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  autoFocus
                  placeholder="Search element…"
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': { background: '#0c1a32', fontSize: '13px', fontFamily: MONO, color: '#eaf0fb', borderRadius: '8px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(120,150,200,.26)' },
                    '& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(120,150,200,.5)' },
                    '& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2dd4bf', borderWidth: '1px' },
                    '& .MuiInputBase-input::placeholder': { color: '#5f7398', opacity: 1 },
                    '& .MuiSvgIcon-root': { color: '#5f7398' },
                  }}
                />
              )}
              slotProps={{
                paper: { sx: { background: '#0d1a33', border: '1px solid rgba(120,150,200,.24)', borderRadius: '10px', mt: '4px', boxShadow: '0 16px 40px rgba(0,0,0,.4)' } },
                listbox: { sx: { p: '4px 0', maxHeight: '200px' } },
              }}
            />
          )}
          {pickerMode === 'canvas' && (
            <Box sx={{ py: '14px', textAlign: 'center' }}>
              <Box sx={{ fontFamily: "'Lexend', sans-serif", fontSize: '12.5px', color: '#8fa3c6', lineHeight: 1.6, mb: '4px' }}>
                Click an element on the drawing to set as {which === 'source' ? 'source' : 'target'}.
              </Box>
              <Box sx={{ fontSize: '11px', color: '#5f7398', fontFamily: "'Lexend', sans-serif" }}>
                Hover to preview the new link.
              </Box>
            </Box>
          )}
        </Box>

        {/* Add new element footer */}
        <Box sx={{ borderTop: '1px solid rgba(120,150,200,.12)', p: '8px 12px' }}>
          <Box component="button"
            onClick={() => {
              if (onAddElementForEndpoint) onAddElementForEndpoint({ connectionId, which });
              handleCancelPicker();
            }}
            sx={{ width: '100%', py: '9px', background: 'transparent', border: '1.5px dashed rgba(45,212,191,.4)', borderRadius: '8px', color: '#2dd4bf', fontSize: '12.5px', fontWeight: 500, cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { background: 'rgba(45,212,191,.06)', borderColor: '#2dd4bf' } }}>
            + Add new element on the drawing
          </Box>
        </Box>
      </Box>
    );
  };

  // ── Connected-element card (Element view → Connected section) ─────────────
  const renderConnectedElementCard = (conn) => {
    const iAmSource = String(getIdValue(conn.source)) === String(selectedElement._id);
    const otherId = iAmSource ? getIdValue(conn.target) : getIdValue(conn.source);
    const resolved = resolveEndpoint(otherId);
    const other = resolved?.doc;
    // A 'labels' edge's far endpoint may be a Connection (naming a pipe),
    // not an Element (Phase 1.5) — the endpoint picker only supports
    // re-pointing to a different Element, so "Change element" doesn't apply.
    const otherIsConnection = resolved?.kind === 'connection';
    const farEndpoint = iAmSource ? 'target' : 'source';
    const isEditingThis = pickerConnectionId === conn._id && editingEndpoint !== null;

    if (isEditingThis) {
      return <Box key={conn._id}>{renderEndpointPicker(conn._id, editingEndpoint)}</Box>;
    }

    return (
      <Box key={conn._id} sx={{ borderRadius: '11px', border: '1px solid rgba(120,150,200,.2)', overflow: 'hidden', background: 'rgba(120,150,200,.03)' }}>
        {/* Identity */}
        <Box sx={{ px: '13px', py: '11px' }}>
          <Box sx={{ fontSize: '13px', fontWeight: 500, color: '#cdd9ee', mb: '3px' }}>
            {conn.relationshipType || 'flows to'}{!iAmSource ? ' (in)' : ''}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Box sx={{ fontFamily: MONO, fontSize: '12px', color: '#5fe6d6' }}>{other?.name || getElementName(otherId)}</Box>
            <Box sx={{ fontSize: '11px', color: '#5f7398' }}>·</Box>
            <Box sx={{ fontSize: '12px', color: '#8fa3c6' }}>{otherIsConnection ? 'Pipe' : getLabelForType(other?.elementType || 'node')}</Box>
          </Box>
        </Box>
        {/* Action bar */}
        <Box sx={{ display: 'flex', borderTop: '1px solid rgba(120,150,200,.12)' }}>
          {!otherIsConnection && (
            <>
              <ActionBtn accent onClick={() => handleOpenPicker(conn._id, farEndpoint)}>✎ Change element</ActionBtn>
              <Box sx={{ width: '1px', background: 'rgba(120,150,200,.12)', flexShrink: 0 }} />
            </>
          )}
          <ActionBtn onClick={() => handleConnectionSelect(conn)}>Open connection →</ActionBtn>
          <Box sx={{ width: '1px', background: 'rgba(120,150,200,.12)', flexShrink: 0 }} />
          <Tooltip title="Delete this connection">
            <Box component="button" aria-label="Delete connection" onClick={() => handleDeleteConnection(conn, { keepSelection: true })}
              sx={{ flexShrink: 0, background: 'transparent', border: 'none', color: 'rgba(244,113,106,.55)', cursor: 'pointer', display: 'flex', alignItems: 'center', px: '12px', '&:hover': { color: '#f4716a', background: 'rgba(244,113,106,.08)' } }}>
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  // ── Endpoint card (Connection view → FROM / TO) ────────────────────────────
  const renderEndpointCard = (which) => {
    const endId = getIdValue(selectedConnection[which]);
    const resolved = resolveEndpoint(endId);
    const el = resolved?.doc;
    // TO may resolve to a Connection for a 'labels' edge naming a pipe
    // (Phase 1.5) — FROM never does (a label's source is always an Element).
    const isConnectionEndpoint = resolved?.kind === 'connection';
    const isEditingThis = pickerConnectionId === selectedConnection._id && editingEndpoint === which;

    if (isEditingThis) {
      return renderEndpointPicker(selectedConnection._id, which);
    }

    return (
      <Box sx={{ borderRadius: '11px', border: '1px solid rgba(120,150,200,.2)', overflow: 'hidden', background: 'rgba(120,150,200,.03)' }}>
        {/* Identity */}
        <Box sx={{ px: '13px', py: '11px' }}>
          <Box sx={{ mb: '7px' }}>
            <Box component="span" sx={{ fontFamily: MONO, fontSize: '10px', letterSpacing: '.1em', textTransform: 'uppercase', px: '7px', py: '3px', borderRadius: '999px', background: 'rgba(245,165,36,.12)', border: '1px solid rgba(245,165,36,.3)', color: '#f5a524' }}>
              {which === 'source' ? 'FROM' : 'TO'}
            </Box>
          </Box>
          <Box sx={{ fontFamily: MONO, fontSize: '13px', color: '#5fe6d6', mb: '2px' }}>{el?.name || getElementName(endId)}</Box>
          <Box sx={{ fontSize: '12px', color: '#8fa3c6' }}>{isConnectionEndpoint ? 'Pipe' : getLabelForType(el?.elementType || 'node')}</Box>
        </Box>
        {/* Action bar */}
        <Box sx={{ display: 'flex', borderTop: '1px solid rgba(120,150,200,.12)' }}>
          {!isConnectionEndpoint && (
            <>
              <ActionBtn accent onClick={() => handleOpenPicker(selectedConnection._id, which)}>✎ Change element</ActionBtn>
              <Box sx={{ width: '1px', background: 'rgba(120,150,200,.12)', flexShrink: 0 }} />
            </>
          )}
          <ActionBtn onClick={() => {
            if (!el) return;
            if (isConnectionEndpoint) handleConnectionSelect(el); else handleElementSelect(el);
          }}>{isConnectionEndpoint ? 'Go to connection →' : 'Go to element →'}</ActionBtn>
        </Box>
      </Box>
    );
  };

  // ── Reject control — a single always-visible reason picker, no dialog ──────
  // Shared by the element and connection edit forms (§0.2 items 5/6): a
  // rejected item preserves its wrong bbox/class as hard-negative training
  // signal instead of being deleted, so this is deliberately a soft toggle
  // with a required reason rather than a plain delete-style action.
  // One click opens the native <select> (annotators do this thousands of
  // times — the reason picker used to be a second UI state behind a separate
  // "Reject" button click, which was one click too many at that volume).
  const renderRejectControl = (item, { onReject, onUnreject, entityType }) => {
    const isRejected = item?.metadata?.rejected;
    if (isRejected) {
      return (
        <Box component="button" onClick={onUnreject}
          sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', background: 'rgba(244,113,106,.08)', color: '#f4a39e', fontWeight: 600, fontSize: '13px', py: '11px', border: '1.5px solid rgba(244,113,106,.4)', borderRadius: '10px', cursor: 'pointer', mb: '10px', fontFamily: "'Lexend', sans-serif", '&:hover': { borderColor: 'rgba(244,113,106,.7)', color: '#f4716a' } }}>
          ✕ Rejected · {FAILURE_MODE_LABELS[item.metadata.rejectionReason] || item.metadata.rejectionReason} — Undo
        </Box>
      );
    }
    return (
      <Box
        component="select"
        defaultValue=""
        onChange={(e) => { const reason = e.target.value; e.target.value = ''; if (reason) onReject(reason); }}
        sx={{ width: '100%', display: 'block', textAlign: 'center', background: 'transparent', color: '#8fa3c6', fontWeight: 500, fontSize: '13px', py: '11px', border: '1.5px solid rgba(120,150,200,.24)', borderRadius: '10px', cursor: 'pointer', mb: '10px', fontFamily: "'Lexend', sans-serif", outline: 'none', '&:hover': { borderColor: 'rgba(244,113,106,.5)', color: '#f4716a' } }}
      >
        <option value="" disabled>Reject…</option>
        {SORTED_FAILURE_MODES_BY_ENTITY[entityType].map(fm => <option key={fm.value} value={fm.value}>{fm.label}</option>)}
      </Box>
    );
  };

  // ── Name-field text_label picker (Phase 1.5) ────────────────────────────────
  // The plain Name text field (in both edit forms below) is untouched and
  // stays the default path — labels are sparse on real drawings, so typing a
  // name must never auto-create a text_label. This is an *additional*,
  // optional icon button next to it: pick an existing text_label to link,
  // which creates/re-points a `labels` connection and derives the name from
  // the label's own text (server-side, with undo).
  const openNamePickerButton = (onClick) => (
    <Box sx={{ mt: '-8px', mb: '14px' }}>
      <Box component="button" onClick={onClick} title="Link an existing text label"
        sx={{ background: 'transparent', border: '1px solid rgba(120,150,200,.24)', color: '#7f93b8', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', px: '8px', py: '4px', borderRadius: '7px', fontSize: '11.5px', fontFamily: "'Lexend', sans-serif", '&:hover': { color: '#2dd4bf', borderColor: 'rgba(45,212,191,.5)' } }}>
        <LabelOutlinedIcon sx={{ fontSize: 14 }} /> Link text label
      </Box>
    </Box>
  );

  const renderNamePicker = (targetItem, targetType, { pickerOpen, setPickerOpen }) => {
    if (!pickerOpen) return openNamePickerButton(() => setPickerOpen(true));

    const labelOptions = rankTextLabelOptions({ elements, connections, target: targetItem, targetType });
    // Matches legacy 'labels' connections too (not just 'names') so re-picking
    // via this UI re-points — and upgrades — an old-style edge instead of
    // leaving it in place and creating a second, parallel 'names' connection.
    const existingLabelConn = connections.find(
      c => ['labels', 'names'].includes(c.relationshipType) && !c.metadata?.rejected && String(getIdValue(c.target)) === String(targetItem._id)
    );

    // Linking a text label only ever changes `name` — but setSelectedElement/
    // setSelectedConnection below hands the effect above (line ~189) a new object
    // reference, which re-syncs the *entire* edit form from it, including fields
    // the CV pipeline set that the user hasn't saved over yet (equipment type,
    // description). Carry the current in-progress edits (editElementData /
    // editConnectionData) forward into that object so the re-sync is a no-op for
    // everything except the name it's actually meant to change.
    const applyLocalNameUpdate = (newName) => {
      if (targetType === 'Element') {
        setElements(prev => prev.map(el => el._id === targetItem._id ? { ...el, name: newName } : el));
        if (selectedElement?._id === targetItem._id) {
          setSelectedElement(prev => ({ ...prev, name: newName, description: editElementData.description, elementType: editElementData.elementType }));
          setEditElementData(prev => ({ ...prev, name: newName }));
        }
      } else {
        setConnections(prev => prev.map(c => c._id === targetItem._id ? { ...c, name: newName } : c));
        if (selectedConnection?._id === targetItem._id) {
          setSelectedConnection(prev => ({ ...prev, name: newName, description: editConnectionData.description, relationshipType: editConnectionData.relationshipType }));
          setEditConnectionData(prev => ({ ...prev, name: newName }));
        }
      }
    };

    const handlePick = async (label) => {
      setPickerOpen(false);
      // On an element the pick is held like an accepted suggestion: the name
      // field shows it (amber link icon) and the link is written on Save /
      // Confirm, or dropped on Cancel — one way of working for every name edit.
      if (targetType === 'Element' && selectedElement?._id === targetItem._id) {
        const already = linkedNameLabel_ && String(linkedNameLabel_._id) === String(label._id);
        const pending = already ? null : label;
        const d = { ...editElementData, name: label.name || '' };
        setEditElementData(d);
        setPendingNameLink(pending);
        setElementFormDirty(checkElementFormDirty(d, pending));
        return;
      }
      try {
        // Any in-progress, unsaved edits (e.g. equipment type) must be
        // flushed to the server before linking — applyLocalNameUpdate below
        // only patches local state to *look* right, so a still-dirty field
        // would otherwise silently revert on the next real fetch despite
        // appearing correct here.
        if (targetType === 'Element' && elementFormDirty && selectedElement?._id === targetItem._id) {
          const saved = await handleSaveElement({ silent: true });
          if (!saved) return; // handleSaveElement already notified the failure
        } else if (targetType === 'Connection' && connectionFormDirty && selectedConnection?._id === targetItem._id) {
          const saved = await handleSaveConnection({ silent: true });
          if (!saved) return;
        }

        // Choosing a text_label from this picker always means "this is the
        // target's name" by construction (the list is filtered to text_label
        // elements only) — so this is unconditionally a 'names' connection,
        // never 'describes' (that type has no dedicated creation UI yet — see
        // PIPELINE_IMPROVEMENT_PLAN.md).
        const { data } = existingLabelConn
          ? await api.put(`/api/connections/${existingLabelConn._id}`, { source: label._id, relationshipType: 'names' })
          : await api.post('/api/connections', {
              drawing: drawingId,
              source: label._id,
              target: targetItem._id,
              targetType,
              relationshipType: 'names',
              confirmed: true,
            });
        setConnections(prev => existingLabelConn
          ? prev.map(c => c._id === data._id ? data : c)
          : [...prev, data]);
        if (data?.replacedLinks?.length) setConnections(prev => dropReplacedLinks(prev, data));

        const overwrite = data?.nameOverwrite;
        if (overwrite) {
          applyLocalNameUpdate(overwrite.newName);
          notify(`Renamed "${overwrite.oldName || '—'}" → "${overwrite.newName}"`, {
            undo: async () => {
              const endpoint = targetType === 'Element' ? 'elements' : 'connections';
              const { data: reverted } = await api.put(`/api/${endpoint}/${targetItem._id}`, { name: overwrite.oldName });
              applyLocalNameUpdate(reverted.name);
            },
          });
        } else {
          notify(`Linked "${label.name || 'label'}"`);
        }
      } catch {
        notify('Failed to link label. Please try again.', { tone: 'error' });
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '14px' }}>
        <Autocomplete
          openOnFocus
          autoHighlight
          size="small"
          fullWidth
          options={labelOptions}
          getOptionLabel={(entry) => entry.label.name || '(unnamed label)'}
          isOptionEqualToValue={(a, b) => a.label._id === b.label._id}
          onChange={(_, picked) => { if (picked) handlePick(picked.label); }}
          renderOption={renderTextLabelOption}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              placeholder="Search text labels…"
              sx={{
                '& .MuiInputBase-root': { background: '#0c1a32', fontSize: '13px', fontFamily: MONO, color: '#eaf0fb', borderRadius: '8px' },
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(120,150,200,.26)' },
                '& .MuiInputBase-input::placeholder': { color: '#5f7398', opacity: 1 },
              }}
            />
          )}
          slotProps={{
            paper: { sx: { background: '#0d1a33', border: '1px solid rgba(120,150,200,.24)', borderRadius: '10px', mt: '4px' } },
          }}
        />
        <Box component="button" onClick={() => setPickerOpen(false)}
          sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '14px', p: '2px 5px', borderRadius: '4px', lineHeight: 1, flexShrink: 0, '&:hover': { color: '#eaf0fb' } }}>
          ✕
        </Box>
      </Box>
    );
  };

  // ── Element edit form ──────────────────────────────────────────────────────
  // Rapid-annotate: what the assist made of this element — the read to check
  // before confirming. Symbol tool: Claude's class, cited standard, any proposed
  // new type. Text tool: the OCR'd text. Or why it failed.
  const renderVlmPanel = (element) => {
    const vlm = element?.metadata?.vlm;
    if (!isAdmin || !vlm) return null;
    const state = getVlmState(element);
    const isOcr = vlm.task === 'ocr';
    const accent = state ? VLM_COLORS[state] : '#7f93b8';
    const askBtn = (label) => onVlmClassify && (
      <Box component="button" onClick={() => onVlmClassify(element)}
        sx={{ background: 'transparent', border: `1px solid ${accent}80`, color: accent, fontSize: '11.5px', px: '9px', py: '3px', borderRadius: '6px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { background: `${accent}1a` } }}>
        {label}
      </Box>
    );
    return (
      <Box sx={{ mx: '16px', mt: '12px', p: '10px 12px', borderRadius: '9px', border: `1px solid ${accent}55`, background: `${accent}0f`, fontSize: '12.5px', color: '#cdd9ee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '4px' }}>
          <Box sx={{ fontFamily: MONO, fontSize: '10.5px', color: accent, letterSpacing: '.04em' }}>{isOcr ? 'OCR READ' : 'AI DETECTION'}</Box>
          {vlm.status === 'failed' ? askBtn('Retry') : vlm.status === 'done' ? askBtn(isOcr ? 'Read again' : 'Ask again') : null}
        </Box>
        {vlm.status === 'pending' && <Box sx={{ color: '#a9bcdc' }}>{isOcr ? 'Reading…' : 'Classifying…'}</Box>}
        {vlm.status === 'failed' && <Box sx={{ color: '#f9a8d4', wordBreak: 'break-word' }}>{vlm.error}</Box>}
        {vlm.status === 'done' && isOcr && (
          <>
            {vlm.text
              ? <Box sx={{ fontFamily: MONO, color: '#5fe6d6', fontSize: '13.5px', wordBreak: 'break-word' }}>{vlm.text}</Box>
              : <Box sx={{ color: '#a9bcdc', fontStyle: 'italic' }}>No text found — type it in Tag / name.</Box>}
            <Box sx={{ color: '#7f93b8', mt: '4px', fontSize: '11px' }}>{vlm.engine || 'ocr'} · check O/0 and I/1</Box>
          </>
        )}
        {vlm.status === 'done' && !isOcr && (
          <>
            <Box sx={{ fontWeight: 600, color: '#f4f8ff' }}>
              {vlm.symbol_class === 'other'
                ? <>New type? {vlm.proposed_label || vlm.proposed_class} <Box component="span" sx={{ fontFamily: MONO, color: '#8fa3c6', fontWeight: 400 }}>{vlm.proposed_class}</Box></>
                : vlm.symbol_class === 'not_a_symbol' ? 'Not a symbol' : getLabelForType(vlm.symbol_class)}
              {vlm.tag_text && <Box component="span" sx={{ fontFamily: MONO, color: '#5fe6d6', fontWeight: 400, ml: '8px' }}>{vlm.tag_text}</Box>}
            </Box>
            {vlm.description && <Box sx={{ fontStyle: 'italic', color: '#a9bcdc', mt: '3px' }}>{vlm.description}</Box>}
            {vlm.standard_reference && <Box sx={{ color: '#8fa3c6', mt: '4px', fontSize: '11.5px' }}>📐 {vlm.standard_reference}</Box>}
          </>
        )}
      </Box>
    );
  };

  // Pinned footer of the element pane: always at the same screen position
  // however long the content above gets (VLM read, links, long names), so
  // a review pass can keep the pointer parked on Confirm. Confirm is last —
  // the footer grows upward, so only the bottom-most button never moves.
  const renderElementActions = () => (
    <Box sx={{ flex: 'none', px: '16px', pt: '12px', pb: '14px', borderTop: '1px solid rgba(120,150,200,.16)', background: '#0b1730', boxShadow: '0 -10px 18px rgba(4,10,24,.45)' }}>
      {elementFormDirty && (
        <Box component="button" onClick={() => { handleSaveElement(); setElementFormDirty(false); }}
          sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5a524', color: '#241200', fontWeight: 600, fontSize: '14px', py: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer', mb: '8px', boxShadow: '0 8px 22px rgba(245,165,36,.26)', '&:hover': { background: '#ffb84d' }, fontFamily: "'Lexend', sans-serif" }}>
          Save changes
        </Box>
      )}
      {elementFormDirty && (
        <Box component="button"
          onClick={() => { setEditElementData({ name: selectedElement.name || '', description: selectedElement.description || '', elementType: selectedElement.elementType || 'node', confirmed: editElementData.confirmed }); setPendingNameLink(null); setPendingDescribes([]); setElementFormDirty(false); }}
          sx={{ width: '100%', background: 'transparent', border: '1px solid rgba(120,150,200,.26)', color: '#cdd9ee', fontWeight: 500, fontSize: '13px', py: '9px', borderRadius: '9px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#fff' }, mb: '8px' }}>
          Cancel
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ flex: 1, minWidth: 0, '& > *': { mb: '0 !important' } }}>
          {isAdmin && renderRejectControl(selectedElement, {
            onReject: handleRejectElement,
            onUnreject: handleUnrejectElement,
            entityType: 'element',
          })}
        </Box>
        <Box component="button" onClick={() => handleDeleteElement(selectedElement)} title="Delete element"
          sx={{ background: 'transparent', border: 'none', color: 'rgba(244,113,106,.55)', cursor: 'pointer', display: 'flex', alignItems: 'center', p: '5px 6px', borderRadius: '6px', '&:hover': { color: '#f4716a', background: 'rgba(244,113,106,.1)' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>
      <Box component="button" onClick={handleConfirmElementToggle}
        sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', background: 'transparent', color: editElementData.confirmed ? '#44d07b' : '#f5a524', fontWeight: 600, fontSize: '15px', py: '13px', border: editElementData.confirmed ? '1.5px solid rgba(68,208,123,.5)' : '1.5px solid rgba(245,165,36,.6)', borderRadius: '10px', cursor: 'pointer', mt: '10px', fontFamily: "'Lexend', sans-serif", transition: 'border-color .15s, color .15s', '&:hover': editElementData.confirmed ? { borderColor: 'rgba(68,208,123,.8)', color: '#55e08d' } : { borderColor: '#f5a524', color: '#ffb84d' } }}>
        {editElementData.confirmed ? '✓ Confirmed' : 'Confirm'}
      </Box>
    </Box>
  );

  // Why the selected item is flagged by "Show unlinked" — names the missing link.
  const renderUnlinkedNotice = (reason) => reason && (
    <Box sx={{ mx: '16px', mt: '12px', p: '8px 12px', borderRadius: '9px', border: '1px dashed rgba(56,189,248,.55)', background: 'rgba(56,189,248,.07)', fontSize: '12.5px', color: '#9adcfb' }}>
      <Box component="span" sx={{ fontFamily: MONO, fontSize: '10.5px', color: '#38bdf8', letterSpacing: '.04em', mr: '8px' }}>UNLINKED</Box>
      {reason}
    </Box>
  );

  const renderNameLinkIcon = () => {
    const pending = pendingNameLink && editElementData.name === (pendingNameLink.name || '');
    const linked = !pending && linkedNameLabel_ && editElementData.name === (linkedNameLabel_.name || '');
    if (!pending && !linked) return null;
    return (
      <Tooltip title={pending ? `Will link to the text "${pendingNameLink.name}" when you Save or Confirm` : `Linked to the text label "${linkedNameLabel_.name}"`}>
        <LinkIcon sx={{ fontSize: 18, color: pending ? '#f5a524' : '#2dd4bf' }} />
      </Tooltip>
    );
  };

  const acceptNameSuggestion = () => {
    const label = nameSuggestion.label;
    const d = { ...editElementData, name: label.name || '' };
    setEditElementData(d);
    setPendingNameLink(label);
    setElementFormDirty(checkElementFormDirty(d, label));
  };

  const renderNameSuggestion = () => {
    if (!nameSuggestion || pendingNameLink || dismissedSuggestionId === String(nameSuggestion.label._id)) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mt: '-8px', mb: '14px', p: '6px 8px 6px 10px', borderRadius: '8px', border: '1px dashed rgba(167,139,250,.55)', background: 'rgba(167,139,250,.07)', fontSize: '12.5px', color: '#cdd9ee' }}>
        <LinkIcon sx={{ fontSize: 16, color: '#a78bfa', flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {nameSuggestion.close
            ? <>Nearby text <Box component="span" sx={{ fontFamily: MONO, color: '#c4b5fd' }}>{nameSuggestion.reading}</Box> is a close match to its tag <Box component="span" sx={{ fontFamily: MONO, color: '#8fa3c6' }}>{nameSuggestion.tag}</Box> — use as name?</>
            : <>Nearby text <Box component="span" sx={{ fontFamily: MONO, color: '#c4b5fd' }}>{nameSuggestion.reading}</Box> matches its tag — use as name?</>}
        </Box>
        <Tooltip title="Use this text as the name and link it (saved with Save or Confirm)">
          <Box component="button" onClick={acceptNameSuggestion}
            sx={{ background: '#a78bfa', border: 'none', color: '#1a0f33', fontWeight: 700, fontSize: '12px', px: '9px', py: '3px', borderRadius: '6px', cursor: 'pointer', '&:hover': { background: '#c4b5fd' } }}>✓</Box>
        </Tooltip>
        <Tooltip title="Not this one">
          <Box component="button" onClick={() => setDismissedSuggestionId(String(nameSuggestion.label._id))}
            sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '14px', p: '2px 4px', '&:hover': { color: '#eaf0fb' } }}>✕</Box>
        </Tooltip>
      </Box>
    );
  };

  const setDescribes = (next) => {
    setPendingDescribes(next);
    setElementFormDirty(checkElementFormDirty(editElementData, pendingNameLink, next));
  };

  const describeChip = (label, accepted) => (
    <Box key={label._id} sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', pl: '8px', pr: '3px', py: '2px', borderRadius: '6px', border: `1px ${accepted ? 'solid' : 'dashed'} ${accepted ? 'rgba(245,165,36,.6)' : 'rgba(167,139,250,.6)'}`, background: accepted ? 'rgba(245,165,36,.08)' : 'rgba(167,139,250,.08)' }}>
      <Box component="span" sx={{ fontFamily: MONO, fontSize: '12px', color: accepted ? '#f5c46b' : '#c4b5fd' }}>{label.name}</Box>
      {accepted ? (
        <Tooltip title="Don't link this one">
          <Box component="button" onClick={() => setDescribes(pendingDescribes.filter(l => l._id !== label._id))}
            sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '13px', p: '0 4px', '&:hover': { color: '#eaf0fb' } }}>×</Box>
        </Tooltip>
      ) : (
        <Tooltip title="Link as a description (saved with Save or Confirm)">
          <Box component="button" onClick={() => setDescribes([...pendingDescribes, label])}
            sx={{ background: '#a78bfa', border: 'none', color: '#1a0f33', fontWeight: 700, fontSize: '11px', px: '6px', py: '1px', borderRadius: '5px', cursor: 'pointer', '&:hover': { background: '#c4b5fd' } }}>✓</Box>
        </Tooltip>
      )}
    </Box>
  );

  const renderDescribeSuggestions = () => {
    if (!describeSuggestions.length && !pendingDescribes.length) return null;
    return (
      <Box sx={{ mt: '-8px', mb: '14px', p: '7px 8px 7px 10px', borderRadius: '8px', border: '1px dashed rgba(167,139,250,.45)', background: 'rgba(167,139,250,.05)', fontSize: '12.5px', color: '#cdd9ee' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '6px' }}>
          <LinkIcon sx={{ fontSize: 16, color: '#a78bfa', flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {describeSuggestions.length ? 'Nearby text repeated across the drawing — link as description?' : 'Will link as descriptions when you Save or Confirm'}
            {describeSuggestions.length > 0 && pendingDescribes.length > 0 && (
              <Box component="span" sx={{ color: '#f5c46b' }}> Amber ones link on Save or Confirm.</Box>
            )}
          </Box>
          {describeSuggestions.length > 0 && (
            <Tooltip title="Not these">
              <Box component="button" onClick={() => setDismissedDescribeIds(ids => [...ids, ...describeSuggestions.map(l => String(l._id))])}
                sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', fontSize: '14px', p: '2px 4px', '&:hover': { color: '#eaf0fb' } }}>✕</Box>
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {pendingDescribes.map(l => describeChip(l, true))}
          {describeSuggestions.map(l => describeChip(l, false))}
        </Box>
      </Box>
    );
  };

  const renderElementEditForm = () => (
    <Box sx={{ px: '16px', pt: '6px', pb: '12px' }}>
      <DarkInput
        label="Tag / name"
        value={editElementData.name}
        autoFocus
        placeholder="e.g. V-12"
        adornment={renderNameLinkIcon()}
        onKeyDown={(e) => { if (e.key === 'Enter' && elementFormDirty) { handleSaveElement(); setElementFormDirty(false); } }}
        onChange={(e) => {
          const d = { ...editElementData, name: e.target.value };
          // Typing a different name drops an accepted-but-unsaved link: the link
          // and the name must agree.
          const pending = pendingNameLink && e.target.value === (pendingNameLink.name || '') ? pendingNameLink : null;
          setPendingNameLink(pending);
          setEditElementData(d);
          setElementFormDirty(checkElementFormDirty(d, pending));
        }}
      />
      {renderNameSuggestion()}
      {selectedElement && renderNamePicker(selectedElement, 'Element', {
        pickerOpen: elementNamePickerOpen,
        setPickerOpen: setElementNamePickerOpen,
      })}
      <EquipmentTypePicker
        value={editElementData.elementType}
        disabled={false}
        onChange={(newType) => { const d = { ...editElementData, elementType: newType }; setEditElementData(d); setElementFormDirty(checkElementFormDirty(d)); }}
      />
      <DarkInput
        label="Description"
        value={editElementData.description}
        multiline rows={2}
        placeholder="Short description…"
        onChange={(e) => { const d = { ...editElementData, description: e.target.value }; setEditElementData(d); setElementFormDirty(checkElementFormDirty(d)); }}
      />
      {renderDescribeSuggestions()}
      {selectedElement && (
        <DescribesPicker
          describesConnections={connections.filter(c => c.relationshipType === 'describes' && String(getIdValue(c.target)) === String(selectedElement._id))}
          labelOptions={rankTextLabelOptions({
            elements, connections, target: selectedElement, targetType: 'Element',
            // already describing this element — linking again would duplicate the edge
            excludeIds: [
              ...connections.filter(c => c.relationshipType === 'describes' && String(getIdValue(c.target)) === String(selectedElement._id)).map(c => getIdValue(c.source)),
              ...pendingDescribes.map(l => l._id),
            ],
          })}
          onLink={(label) => {
            // Held until Save / Confirm, like an accepted description suggestion.
            if (!pendingDescribes.some(l => String(l._id) === String(label._id))) setDescribes([...pendingDescribes, label]);
          }}
          onUnlink={handleUnlinkDescribesConnection}
        />
      )}

    </Box>
  );

  // ── Connection edit form ───────────────────────────────────────────────────
  // Pinned footer of the connection pane: always at the same screen position
  // however long the content above gets (VLM read, links, long names), so
  // a review pass can keep the pointer parked on Confirm. Confirm is last —
  // the footer grows upward, so only the bottom-most button never moves.
  const renderConnectionActions = () => (
    <Box sx={{ flex: 'none', px: '16px', pt: '12px', pb: '14px', borderTop: '1px solid rgba(120,150,200,.16)', background: '#0b1730', boxShadow: '0 -10px 18px rgba(4,10,24,.45)' }}>
      {connectionFormDirty && (
        <Box component="button" onClick={() => { handleSaveConnection(); setConnectionFormDirty(false); }}
          sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5a524', color: '#241200', fontWeight: 600, fontSize: '14px', py: '12px', border: 'none', borderRadius: '10px', cursor: 'pointer', mb: '8px', boxShadow: '0 8px 22px rgba(245,165,36,.26)', '&:hover': { background: '#ffb84d' }, fontFamily: "'Lexend', sans-serif" }}>
          Save changes
        </Box>
      )}
      {connectionFormDirty && (
        <Box component="button"
          onClick={() => { setEditConnectionData({ name: selectedConnection.name || '', description: selectedConnection.description || '', relationshipType: selectedConnection.relationshipType || 'flows to', confirmed: editConnectionData.confirmed }); setConnectionFormDirty(false); }}
          sx={{ width: '100%', background: 'transparent', border: '1px solid rgba(120,150,200,.26)', color: '#cdd9ee', fontWeight: 500, fontSize: '13px', py: '9px', borderRadius: '9px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#fff' }, mb: '8px' }}>
          Cancel
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Box sx={{ flex: 1, minWidth: 0, '& > *': { mb: '0 !important' } }}>
          {isAdmin && renderRejectControl(selectedConnection, {
            onReject: handleRejectConnection,
            onUnreject: handleUnrejectConnection,
            entityType: 'connection',
          })}
        </Box>
        <Box component="button" onClick={() => handleDeleteConnection(selectedConnection)} title="Delete connection"
          sx={{ background: 'transparent', border: 'none', color: 'rgba(244,113,106,.55)', cursor: 'pointer', display: 'flex', alignItems: 'center', p: '5px 6px', borderRadius: '6px', '&:hover': { color: '#f4716a', background: 'rgba(244,113,106,.1)' } }}>
          <DeleteOutlineIcon sx={{ fontSize: 20 }} />
        </Box>
      </Box>
      <Box component="button" onClick={handleConfirmConnectionToggle}
        sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', background: 'transparent', color: editConnectionData.confirmed ? '#44d07b' : '#f5a524', fontWeight: 600, fontSize: '15px', py: '13px', border: editConnectionData.confirmed ? '1.5px solid rgba(68,208,123,.5)' : '1.5px solid rgba(245,165,36,.6)', borderRadius: '10px', cursor: 'pointer', mt: '10px', fontFamily: "'Lexend', sans-serif", transition: 'border-color .15s, color .15s', '&:hover': editConnectionData.confirmed ? { borderColor: 'rgba(68,208,123,.8)', color: '#55e08d' } : { borderColor: '#f5a524', color: '#ffb84d' } }}>
        {editConnectionData.confirmed ? '✓ Confirmed' : 'Confirm'}
      </Box>
    </Box>
  );

  const renderConnectionEditForm = () => (
    <Box sx={{ px: '16px', pt: '6px', pb: '12px' }}>
      <DarkInput
        label="Name"
        value={editConnectionData.name}
        autoFocus
        placeholder="e.g. feed line"
        onKeyDown={(e) => { if (e.key === 'Enter' && connectionFormDirty) { handleSaveConnection(); setConnectionFormDirty(false); } }}
        onChange={(e) => { const d = { ...editConnectionData, name: e.target.value }; setEditConnectionData(d); setConnectionFormDirty(checkConnectionFormDirty(d)); }}
      />
      {selectedConnection && renderNamePicker(selectedConnection, 'Connection', {
        pickerOpen: connectionNamePickerOpen,
        setPickerOpen: setConnectionNamePickerOpen,
      })}
      <Box sx={{ mb: '14px' }}>
        <Box component="label" sx={{ display: 'block', fontFamily: MONO, fontSize: '10.5px', letterSpacing: '.1em', textTransform: 'uppercase', color: '#7f93b8', mb: '6px' }}>Relationship type</Box>
        <Box
          component="select"
          value={editConnectionData.relationshipType}
          onChange={(e) => { const d = { ...editConnectionData, relationshipType: e.target.value }; setEditConnectionData(d); setConnectionFormDirty(checkConnectionFormDirty(d)); }}
          sx={{ width: '100%', background: '#0c1a32', border: '1px solid rgba(120,150,200,.26)', borderRadius: '8px', p: '10px 12px', color: '#eaf0fb', fontSize: '13px', fontFamily: MONO, outline: 'none', '&:focus': { borderColor: '#2dd4bf' } }}
        >
          <option value="flows to">flows to</option>
          <option value="controls">controls</option>
          <option value="names">names</option>
          <option value="describes">describes</option>
          <option value="labels">labels (legacy)</option>
        </Box>
      </Box>
      <DarkInput
        label="Description"
        value={editConnectionData.description}
        multiline rows={2}
        placeholder="Short description…"
        onChange={(e) => { const d = { ...editConnectionData, description: e.target.value }; setEditConnectionData(d); setConnectionFormDirty(checkConnectionFormDirty(d)); }}
      />
      {selectedConnection && (
        <DescribesPicker
          describesConnections={connections.filter(c => c.relationshipType === 'describes' && String(getIdValue(c.target)) === String(selectedConnection._id))}
          labelOptions={rankTextLabelOptions({
            elements, connections, target: selectedConnection, targetType: 'Connection',
            excludeIds: connections.filter(c => c.relationshipType === 'describes' && String(getIdValue(c.target)) === String(selectedConnection._id)).map(c => getIdValue(c.source)),
          })}
          onLink={(label) => handleLinkDescribesLabel(selectedConnection, 'Connection', label)}
          onUnlink={handleUnlinkDescribesConnection}
        />
      )}

    </Box>
  );

  // ── Open sidebar ───────────────────────────────────────────────────────────
  return (
    <>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', width: 344, flexShrink: 0, background: '#0b1730', borderLeft: '1px solid rgba(120,150,200,.16)', zIndex: 1 }}>
        {/* Header */}
        <Box sx={{ flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: '16px', height: '50px', borderBottom: '1px solid rgba(120,150,200,.16)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <Box sx={{ fontFamily: MONO, fontSize: '11px', letterSpacing: '.16em', textTransform: 'uppercase', color: '#2dd4bf', userSelect: 'none' }}>
              {selectedElement || selectedConnection ? 'Selection' : 'Elements & Connections'}
            </Box>
            {(selectedElement || selectedConnection) && (
              <Box component="button" onClick={backToList}
                sx={{ background: 'transparent', border: '1px solid rgba(120,150,200,.26)', color: '#a9bcdc', fontSize: '12px', px: '11px', py: '4px', borderRadius: '7px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { color: '#eaf0fb', borderColor: 'rgba(45,212,191,.5)' } }}>
                ← Back
              </Box>
            )}
            {(selectedElement || selectedConnection) && (
              <Tooltip title="Copy a link to this item" placement="bottom">
                <Box component="button" aria-label="Copy link" onClick={copyItemLink}
                  sx={{ display: 'flex', alignItems: 'center', background: 'transparent', border: '1px solid rgba(120,150,200,.26)', color: '#a9bcdc', px: '7px', py: '4px', borderRadius: '7px', cursor: 'pointer', '&:hover': { color: '#eaf0fb', borderColor: 'rgba(45,212,191,.5)' } }}>
                  <LinkIcon sx={{ fontSize: 15 }} />
                </Box>
              </Tooltip>
            )}
          </Box>
          <Box component="button" onClick={toggleSidebar}
            sx={{ background: 'transparent', border: 'none', color: '#7f93b8', cursor: 'pointer', display: 'flex', alignItems: 'center', '&:hover': { color: '#eaf0fb' } }}>
            <ChevronRightIcon sx={{ fontSize: 18 }} />
          </Box>
        </Box>

        {/* Body */}
        <Box ref={bodyRef} onScroll={onBodyScroll} sx={{ flex: 1, overflowY: 'auto' }}>

          {/* ── SELECTED ELEMENT VIEW ── */}
          {selectedElement && editingElementId === selectedElement._id && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '16px', pt: '16px', pb: '14px', borderBottom: '1px solid rgba(120,150,200,.12)' }}>
                <Tooltip title={selectedElement.name && selectedElement.name.length > 20 ? selectedElement.name : ''} placement="top">
                  <Box sx={{ fontFamily: MONO, fontWeight: 600, fontSize: '15px', color: '#06121f', background: '#2dd4bf', px: '11px', py: '4px', borderRadius: '7px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flexShrink: 1 }}>
                    {selectedElement.name || '—'}
                  </Box>
                </Tooltip>
                <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', px: '9px', py: '4px', borderRadius: '999px', background: getStatusMeta(selectedElement).bg, border: `1px solid ${getStatusMeta(selectedElement).border}`, flexShrink: 0 }}>
                  <StatusDot confirmed={selectedElement.confirmed} rejected={selectedElement.metadata?.rejected} vlm={getVlmState(selectedElement)} />
                  <Box sx={{ fontFamily: MONO, fontSize: '10px', color: getStatusMeta(selectedElement).color }}>{getStatusMeta(selectedElement).label}</Box>
                </Box>
              </Box>

              {renderUnlinkedNotice(unlinked?.elements.get(String(selectedElement._id)))}
              {renderVlmPanel(selectedElement)}

              {renderElementEditForm()}

              {filteredConnections.length > 0 && (
                <Box sx={{ mt: '6px' }}>
                  <SectionHeader label={`Connected (${filteredConnections.length})`} count={`${filteredConnections.filter(c => !c.confirmed).length} unconfirmed`} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', p: '10px 12px' }}>
                    {filteredConnections.map(conn => renderConnectedElementCard(conn))}
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {/* ── SELECTED CONNECTION VIEW ── */}
          {selectedConnection && editingConnectionId === selectedConnection._id && (
            <Box>
              <Box sx={{ px: '16px', pt: '16px', pb: '14px', borderBottom: '1px solid rgba(120,150,200,.12)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '8px' }}>
                  <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#f5a524', flexShrink: 0 }}>→</Box>
                  <Box sx={{ fontSize: '13px', color: '#dce6fa', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedConnection.name || `${getElementName(selectedConnection.source)} → ${getElementName(selectedConnection.target)}`}
                  </Box>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px', px: '9px', py: '4px', borderRadius: '999px', background: getStatusMeta(selectedConnection).bg, border: `1px solid ${getStatusMeta(selectedConnection).border}`, flexShrink: 0 }}>
                    <StatusDot confirmed={selectedConnection.confirmed} rejected={selectedConnection.metadata?.rejected} />
                    <Box sx={{ fontFamily: MONO, fontSize: '10px', color: getStatusMeta(selectedConnection).color }}>{getStatusMeta(selectedConnection).label}</Box>
                  </Box>
                </Box>
                <Box sx={{ fontFamily: MONO, fontSize: '11px', color: '#7f93b8' }}>{selectedConnection.relationshipType || 'flows to'}</Box>
              </Box>

              {renderUnlinkedNotice(unlinked?.connections.get(String(selectedConnection._id)))}
              {renderConnectionEditForm()}

              {/* Endpoints section with Reverse button */}
              <Box sx={{ mt: '6px' }}>
                <SectionHeader
                  label="Endpoints"
                  count={null}
                  action={
                    <Box component="button" onClick={handleReverseDirection}
                      sx={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'transparent', border: '1px solid rgba(120,150,200,.26)', color: '#8fa3c6', fontSize: '11.5px', fontWeight: 500, px: '8px', py: '4px', borderRadius: '7px', cursor: 'pointer', fontFamily: "'Lexend', sans-serif", '&:hover': { borderColor: 'rgba(45,212,191,.5)', color: '#2dd4bf' } }}>
                      <SwapHorizIcon sx={{ fontSize: 14 }} /> Reverse
                    </Box>
                  }
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', p: '10px 12px' }}>
                  {renderEndpointCard('source')}
                  {renderEndpointCard('target')}
                </Box>
              </Box>
            </Box>
          )}

          {/* ── LIST VIEW (nothing selected) ── */}
          {!selectedElement && !selectedConnection && (() => {
            const rawHasAnyItems = filteredElements.length > 0 || filteredConnections.length > 0;
            // Mirrors the canvas's own "Hide rejected" toggle (DrawingView.js) so
            // the side pane list matches what's actually visible on the drawing.
            const notHiddenRejected = item => !hideRejected || !item.metadata?.rejected;
            const baseElements = filteredElements.filter(notHiddenRejected);
            const baseConnections = filteredConnections.filter(notHiddenRejected);
            const typed = applyTypeFilters(baseElements, baseConnections,
              { elementType: listElementType, connectionType: listConnectionType });
            const visibleElements = listMode === 'connections' ? []
              : typed.elements.filter(el => matchesStatus(el, listStatus) && elementMatchesSearch(el, listSearch));
            const visibleConnections = listMode === 'elements' ? []
              : typed.connections.filter(conn => matchesStatus(conn, listStatus) && connectionMatchesSearch(conn, listSearch));
            listOrderRef.current = [...visibleElements.map(item => ({ kind: 'element', item })),
              ...visibleConnections.map(item => ({ kind: 'connection', item }))];
            const hasAnyItems = baseElements.length > 0 || baseConnections.length > 0;
            const filtering = !!(listSearch || listElementType || listConnectionType || listStatus !== 'all');
            const filterNote = [listSearch && `"${listSearch}"`, listStatus !== 'all' && listStatus,
              listElementType && getLabelForType(listElementType), listConnectionType].filter(Boolean).join(' + ');
            return (
            <>
              {hasAnyItems && (
                <>
                  <ListSearchInput listKeys value={listSearch} onChange={setListSearch} placeholder="Search elements & connections…"
                    trailing={<FilterToggleButton open={listFiltersOpen} onClick={() => setListFiltersOpen(o => !o)}
                      active={(listMode !== 'both') + (listStatus !== 'all') + !!listElementType + !!listConnectionType} />} />
                  {listFiltersOpen && <ListFilterBar mode={listMode} onMode={setListMode} status={listStatus} onStatus={setListStatus}
                    elementType={listElementType} onElementType={setListElementType}
                    elementOptions={typeCounts(baseElements, 'elementType')}
                    connectionType={listConnectionType} onConnectionType={setListConnectionType}
                    connectionOptions={typeCounts(baseConnections, 'relationshipType')} />}
                </>
              )}

              {visibleElements.length > 0 && (
                <Box>
                  <SectionHeader label="Elements" count={filtering ? `${visibleElements.length} / ${baseElements.length}` : baseElements.length} />
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {visibleElements.slice(0, listLimit).map(el => (
                      <ElementListRow key={el._id} el={el} onPreview={onPreviewListElement} onOpen={onSelectListElement}
                        previewed={previewItem?.kind === 'element' && previewItem.id === String(el._id)} />
                    ))}
                    {visibleElements.length > listLimit && (
                      <LoadMoreRows remaining={visibleElements.length - listLimit} onMore={showMoreRows} />
                    )}
                  </Box>
                </Box>
              )}

              {visibleConnections.length > 0 && (
                <Box>
                  <SectionHeader label="Connections" count={filtering ? `${visibleConnections.length} / ${baseConnections.length}` : baseConnections.length} />
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {visibleConnections.slice(0, listLimit).map(conn => (
                      <ConnectionListRow key={conn._id} conn={conn} onPreview={onPreviewListConnection} onOpen={onSelectListConnection}
                        previewed={previewItem?.kind === 'connection' && previewItem.id === String(conn._id)}
                        label={conn.name || `${getElementName(conn.source)} → ${getElementName(conn.target)}`} />
                    ))}
                    {visibleConnections.length > listLimit && (
                      <LoadMoreRows remaining={visibleConnections.length - listLimit} onMore={showMoreRows} />
                    )}
                  </Box>
                </Box>
              )}

              {!rawHasAnyItems && (
                <Box sx={{ p: '32px 24px', textAlign: 'center', fontFamily: MONO, fontSize: '12px', color: '#5f7398' }}>
                  {'// no elements detected yet'}
                </Box>
              )}

              {rawHasAnyItems && !hasAnyItems && (
                <Box sx={{ p: '32px 24px', textAlign: 'center', fontFamily: MONO, fontSize: '12px', color: '#5f7398' }}>
                  {'// all items rejected — toggle "Hide rejected" off to review them'}
                </Box>
              )}

              {hasAnyItems && visibleElements.length === 0 && visibleConnections.length === 0 && (
                <Box sx={{ p: '32px 24px', textAlign: 'center', fontFamily: MONO, fontSize: '12px', color: '#5f7398' }}>
                  {filtering ? `nothing matches ${filterNote}` : `no ${listMode} to show`}
                </Box>
              )}
            </>
            );
          })()}
        </Box>

        {/* Pinned actions footer for the current selection (outside the scrolling body) */}
        {selectedElement && editingElementId === selectedElement._id && renderElementActions()}
        {selectedConnection && editingConnectionId === selectedConnection._id && renderConnectionActions()}
      </Box>
    </>
  );
});

export default DrawingSidePane;
